
      import { LightweightChart } from "./LightweightChart.ts";
      import { LineType } from "lightweight-charts";
      import {
        signInWithGoogle,
        signOut,
        onAuthChange,
        getCurrentUser
      } from "./firebase.ts";
      import { websocketService } from "./websocket.ts";

      // Suppress verbose logging in production - override console.log to no-op
      const _originalConsoleLog = console.log;
      console.log = () => {};

      // Auth state
      let currentUser = null;
      let wsConnected = false;
      // Populated by initializeApp(); awaited by auth/websocket startup to avoid refresh races.
      let watchlistLoadPromise = Promise.resolve();

      // Auth functions
      function showApp() {
        document.getElementById("authScreen").classList.add("hidden");
        document.getElementById("userProfile").style.display = "flex";
      }

      function hideApp() {
        document.getElementById("authScreen").classList.remove("hidden");
        document.getElementById("userProfile").style.display = "none";
      }

      async function updateUserProfile(user) {
        currentUser = user;
        if (user) {
          document.getElementById("userAvatar").src =
            user.photoURL || "https://via.placeholder.com/32";
          document.getElementById("userName").textContent =
            user.displayName || user.email;
          document.getElementById("userMenuEmail").textContent = user.email;
          showApp();

          // Connect to WebSocket server with authentication
          try {
            console.log("🔌 Connecting to WebSocket server...");
            await websocketService.connect();
            wsConnected = true;

            // Start keep-alive
            websocketService.startKeepAlive();

            // Subscribe to general channels
            websocketService.subscribe("trades");
            websocketService.subscribe("orders");

            // Setup message handler for real-time updates
            websocketService.addMessageHandler(handleWebSocketMessage);

            console.log("✅ WebSocket connected and subscribed");

            // Wait for watchlist to load (prevents refresh race where auth runs before symbols load)
            await watchlistLoadPromise;

            // Hydrate watchlist ✓ ticks from open trades on refresh
            await hydrateWatchlistActiveTrades();

            // Subscribe to quotes for all watchlist symbols (after WebSocket is connected)
            if (watchlist.length > 0) {
              // Ensure search dialog is closed if it was opened early
              closeSearchDialog();

              watchlist.forEach((item) => {
                websocketService.subscribe(`quotes:${item.symbol}`);
              });
              console.log(
                `✅ Subscribed to quotes for ${watchlist.length} watchlist symbols`
              );

              // Load the first symbol from watchlist now that WebSocket is connected
              if (!currentSymbol) {
                await loadSymbol(watchlist[0].symbol);
              }
            } else {
              console.warn(
                "⚠️ No symbols in watchlist - opening search dialog"
              );
              openSearchDialog();
            }
          } catch (error) {
            console.error("❌ Failed to connect to WebSocket:", error);
          }
        } else {
          hideApp();
          // Disconnect WebSocket on sign out
          if (wsConnected) {
            websocketService.disconnect();
            wsConnected = false;
          }
        }
      }

      // Handle WebSocket messages for real-time updates
      function handleWebSocketMessage(message) {
        console.log("📨 Received WebSocket message:", message.type, message);

        switch (message.type) {
          case "trade":
            // MongoDB change stream - trade document updates
            if (message.data) {
              // Update active trades tracking for watchlist indicator
              if (message.data.status === "open") {
                symbolsWithActiveTrades.add(message.data.symbol);
                updateWatchlistTradeIndicator(message.data.symbol, true);
              } else {
                symbolsWithActiveTrades.delete(message.data.symbol);
                updateWatchlistTradeIndicator(message.data.symbol, false);
              }

              // Process trade updates for current symbol
              if (message.data.symbol === currentSymbol) {
                console.log("📊 Trade update:", message.data);

                // If trade is no longer open, remove all its badges
                if (message.data.status !== "open") {
                  console.log(
                    `🚫 Trade ${message.data.tradeId} is ${message.data.status} - removing badges`
                  );
                  // Remove stop loss badge
                  badgeManager.removeBadge(`stop-${message.data.tradeId}`);
                  // Remove all orders associated with this trade
                  badgeManager.badges.forEach((badgeData, orderId) => {
                    if (badgeData.order.tradeId === message.data.tradeId) {
                      badgeManager.removeBadge(orderId);
                    }
                  });
                  return;
                }
              }
            }

            if (message.data && message.data.symbol === currentSymbol) {
              // Trade updates may contain position info, PnL, etc.
              // Update relevant UI elements
              if (message.data.position) {
                console.log("💼 Position update:", message.data.position);
                // TODO: Update position display with actual position data
              }
              if (message.data.stopLoss) {
                console.log("🛑 Stop loss update:", message.data.stopLoss);
                // Display/update stop loss badge
                if (
                  message.data.stopLoss.level &&
                  message.data.stopLoss.status !== "triggered"
                ) {
                  const stopOrder = {
                    orderId: `stop-${message.data.tradeId}`,
                    tradeId: message.data.tradeId,
                    symbol: message.data.symbol,
                    side:
                      message.data.position?.side === "LONG" ? "sell" : "buy",
                    purpose: "stop",
                    limitPrice: message.data.stopLoss.level,
                    quantity:
                      message.data.stopLoss.remainingQty ||
                      message.data.position?.remainingQty ||
                      0,
                    status: "new"
                  };
                  handleOrderUpdate(stopOrder);
                } else if (message.data.stopLoss.status === "triggered") {
                  // Remove stop loss badge if triggered
                  badgeManager.removeBadge(`stop-${message.data.tradeId}`);
                }
              }
            }
            break;

          case "order":
            // MongoDB change stream - order document updates
            if (message.data && message.data.symbol === currentSymbol) {
              console.log("📝 Order update:", message.data);
              handleOrderUpdate(message.data);
            }
            break;

          case "quote":
            // Redis Pub/Sub - real-time quote updates
            if (message.symbol === currentSymbol) {
              latestQuote = message;
              updatePriceFromQuote(message);
            }

            // Update watchlist item for this symbol
            const watchlistItem = watchlist.find(
              (item) => item.symbol === message.symbol
            );
            if (watchlistItem) {
              const mid =
                (parseFloat(message.ask) + parseFloat(message.bid)) / 2;

              // Set initial price on first quote if not set
              if (
                !watchlistItem.initialPrice ||
                watchlistItem.initialPrice === 0
              ) {
                watchlistItem.initialPrice = mid;
              }

              watchlistItem.price = mid;
              // Calculate change from initial price (opening price of the day)
              watchlistItem.change = mid - watchlistItem.initialPrice;
              updateWatchlistItem(message.symbol, mid, watchlistItem.change);
            }
            break;

          case "market":
            // Redis Pub/Sub - real-time market/indicator updates
            if (message.symbol === currentSymbol && message.data) {
              console.log("📈 Market/Indicator update:", message.data);
              updateIndicatorsFromStream(message.data);
            }
            break;

          case "indicators":
            // Real-time indicator updates
            if (message.symbol === currentSymbol && message.data) {
              console.log("📊 Indicator update:", message.data);
              updateIndicatorsFromStream(message.data);
            }
            break;

          case "command_ack":
            // Command acknowledgment from server
            console.log("✅ Command acknowledged:", message);

            // Find matching pending command by action+symbol
            let matchedKey = null;
            for (const [key, pending] of pendingCommands.entries()) {
              if (
                pending.action === message.action &&
                pending.symbol === message.symbol
              ) {
                matchedKey = key;
                clearTimeout(pending.timeout);
                pending.resolve({
                  commandId: message.commandId,
                  action: message.action,
                  symbol: message.symbol,
                  message: message.message,
                  authenticated: message.authenticated
                });
                break;
              }
            }
            if (matchedKey) {
              pendingCommands.delete(matchedKey);
            }
            break;

          case "command_error":
            // Command error from server
            console.error("❌ Command error:", message);

            // Find matching pending command by action+symbol
            let matchedErrorKey = null;
            for (const [key, pending] of pendingCommands.entries()) {
              if (
                pending.action === message.action &&
                pending.symbol === message.symbol
              ) {
                matchedErrorKey = key;
                clearTimeout(pending.timeout);
                pending.reject(new Error(message.error || "Command failed"));
                break;
              }
            }
            if (matchedErrorKey) {
              pendingCommands.delete(matchedErrorKey);
            } else {
              // Error without matching command - show generic error
              console.error("❌ Command error (no match):", message.error);
            }
            break;

          default:
            console.log("ℹ️ Unhandled message type:", message.type);
        }
      }

      // Setup auth listeners
      function setupAuth() {
        // Google Sign In button
        document
          .getElementById("googleSignInBtn")
          .addEventListener("click", async () => {
            try {
              const btn = document.getElementById("googleSignInBtn");
              btn.disabled = true;
              btn.textContent = "Signing in...";

              await signInWithGoogle();
              // User profile will be updated by onAuthChange
            } catch (error) {
              console.error("Sign in error:", error);
              alert("Failed to sign in. Please try again.");
              const btn = document.getElementById("googleSignInBtn");
              btn.disabled = false;
              btn.innerHTML = `
              <svg class="auth-google-icon" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            `;
            }
          });

        // User profile click
        document
          .getElementById("userProfile")
          .addEventListener("click", (e) => {
            e.stopPropagation();
            const menu = document.getElementById("userMenu");
            menu.classList.toggle("active");
          });

        // Sign out button
        document
          .getElementById("signOutBtn")
          .addEventListener("click", async () => {
            try {
              // Disconnect WebSocket before signing out
              if (wsConnected) {
                console.log("🔌 Disconnecting from WebSocket...");
                websocketService.disconnect();
                wsConnected = false;
              }

              // Destroy badge manager and clean up chart
              badgeManager.destroy();
              if (chart) {
                chart.remove();
                chart = null;
              }

              await signOut();
              document.getElementById("userMenu").classList.remove("active");
            } catch (error) {
              console.error("Sign out error:", error);
            }
          });

        // Close menu on outside click
        document.addEventListener("click", (e) => {
          const menu = document.getElementById("userMenu");
          const profile = document.getElementById("userProfile");
          if (!profile.contains(e.target) && !menu.contains(e.target)) {
            menu.classList.remove("active");
          }
        });

        // Listen to auth state changes
        onAuthChange((user) => {
          console.log("Auth state changed:", user?.email || "signed out");
          updateUserProfile(user);
        });

        // Check initial auth state
        const user = getCurrentUser();
        if (user) {
          updateUserProfile(user);
        }
      }

      // Initialize auth is invoked after initializeApp() is kicked off (see bottom of file)

      let quantity = 1;
      let chartData = [];
      let currentSymbol = null;
      let chart = null;
      let latestQuote = null;
      let currentBar = null; // Current bar being built from quotes/trades
      let currentPrice = null; // Track current market price for PnL
      let contextMenuHandler = null; // Store context menu handler for cleanup
      let contextMenuClickOutsideHandler = null; // Store click-outside handler for cleanup
      let buyButtonHandler = null; // Store buy button handler for cleanup
      let sellButtonHandler = null; // Store sell button handler for cleanup
      let symbolSearchHandler = null; // Store symbol search handler for cleanup
      // Note: Order badges are now managed by BadgeManager (orderLines, isDragging, etc. removed)

      const API_BASE_URL =
        import.meta.env.VITE_API_URL || "http://localhost:3002";

      // Pending commands tracking for WebSocket command/response flow
      const pendingCommands = new Map();

      // ========== API HELPERS ==========
      // Some endpoints (e.g. trades/orders) may require auth. Always attach Firebase ID token if available.
      async function apiFetch(url, options = {}) {
        const headers = new Headers(options.headers || {});
        const user = currentUser || getCurrentUser();
        if (user) {
          try {
            const token = await user.getIdToken();
            headers.set("Authorization", `Bearer ${token}`);
          } catch (e) {
            console.warn(
              "⚠️ Failed to get Firebase ID token for API request:",
              e
            );
          }
        }
        return fetch(url, { ...options, headers });
      }

      // ========== BADGE MANAGER ==========
      // Centralized manager for all chart badges (orders, positions, stop losses)
      class BadgeManager {
        constructor() {
          this.badges = new Map(); // orderId -> badge data
          this.chart = null;
          this.container = null;
          this.isDragging = false;
          this.draggedOrderId = null;
          this.dragStartY = 0;
          this.dragStartPrice = 0;
          this._cleanup = null;
          this.onMoveOrder = null; // Callback for order moves
          this.onCancelOrder = null; // Callback for order cancels
          this.onClosePosition = null; // Callback for position closes
        }

        initialize(chart, container) {
          this.chart = chart;
          this.container = container;
          this._setupDragHandlers();
          this._setupChartUpdateHandlers();
        }

        _setupChartUpdateHandlers() {
          if (!this.chart) return;

          // Update all badge positions when chart scale changes
          const updateAllPositions = () => {
            this.badges.forEach((badgeData) => {
              this._updateBadgePosition(badgeData);
            });
          };

          const chartApi = this.chart.getChart();

          // Subscribe to ALL chart events that affect positioning
          chartApi
            .timeScale()
            .subscribeVisibleLogicalRangeChange(updateAllPositions);

          // Also update on visible data range changes (captures autoscale changes)
          chartApi
            .timeScale()
            .subscribeVisibleTimeRangeChange(updateAllPositions);

          const resizeHandler = () => updateAllPositions();
          window.addEventListener("resize", resizeHandler);

          // Store cleanup function
          this._cleanup = () => {
            window.removeEventListener("resize", resizeHandler);
          };
        }

        _setupDragHandlers() {
          if (!this.container) return;

          this.container.addEventListener("mousedown", (e) =>
            this._onMouseDown(e)
          );
          this.container.addEventListener("mousemove", (e) =>
            this._onMouseMove(e)
          );
          this.container.addEventListener("mouseup", () => this._onMouseUp());
          this.container.addEventListener("mouseleave", () =>
            this._onMouseLeave()
          );
        }

        _onMouseDown(e) {
          if (!this.chart) return;

          const target = e.target.closest(".order-badge");
          if (!target) return;

          const orderId = target.id.replace("order-", "");
          const badgeData = this.badges.get(orderId);

          if (!badgeData) return;

          // Don't allow dragging filled positions
          if (badgeData.order.filled) {
            console.log(`🚫 Cannot move filled position: ${orderId}`);
            return;
          }

          this.isDragging = true;
          this.draggedOrderId = orderId;
          const rect = this.container.getBoundingClientRect();
          this.dragStartY = e.clientY - rect.top;
          this.dragStartPrice = badgeData.order.price;
          console.log(
            `🎯 Started dragging order: ${orderId} from $${badgeData.order.price.toFixed(
              2
            )}`
          );
          e.preventDefault();
        }

        _onMouseMove(e) {
          if (!this.chart || !this.isDragging || !this.draggedOrderId) return;

          const rect = this.container.getBoundingClientRect();
          const y = e.clientY - rect.top;
          const series = this.chart.getSeries();
          const newPrice = series.coordinateToPrice(y);

          if (newPrice && newPrice > 0) {
            const badgeData = this.badges.get(this.draggedOrderId);
            if (badgeData) {
              badgeData.order.price = newPrice;
              this._updateBadgePosition(badgeData);
            }
          }
        }

        async _onMouseUp() {
          if (!this.isDragging || !this.draggedOrderId) return;

          const badgeData = this.badges.get(this.draggedOrderId);
          if (!badgeData) {
            this.isDragging = false;
            this.draggedOrderId = null;
            return;
          }

          const newPrice = parseFloat(badgeData.order.price.toFixed(2));
          const oldPrice = parseFloat(this.dragStartPrice.toFixed(2));
          const orderId = this.draggedOrderId;
          const order = badgeData.order;

          console.log(
            `✅ Finished dragging order: ${orderId} from $${oldPrice} to $${newPrice}`
          );

          this.isDragging = false;
          this.draggedOrderId = null;

          // Don't send command if price didn't change
          if (Math.abs(newPrice - oldPrice) < 0.01) {
            console.log("⚠️ Price unchanged, no update sent");
            return;
          }

          // Emit event instead of direct command
          if (this.onMoveOrder) {
            try {
              await this.onMoveOrder(order, newPrice, oldPrice);
            } catch (error) {
              console.error(`❌ Failed to update order:`, error);
              // Revert price on error
              badgeData.order.price = oldPrice;
              this._updateBadgePosition(badgeData);
            }
          } else {
            // Fallback to direct command if no callback
            try {
              if (order.type === "stop") {
                await sendTradingCommand("move-stop", currentSymbol, {
                  newStopPrice: newPrice
                });
                console.log(`✅ Move stop command sent: $${newPrice}`);
              } else {
                await sendTradingCommand("update-order", currentSymbol, {
                  orderId: orderId,
                  newPrice: newPrice
                });
                console.log(`✅ Update order command sent: $${newPrice}`);
              }
            } catch (error) {
              console.error(`❌ Failed to update order:`, error);
              // Revert price on error
              badgeData.order.price = oldPrice;
              this._updateBadgePosition(badgeData);
            }
          }
        }

        _onMouseLeave() {
          if (this.isDragging) {
            this.isDragging = false;
            this.draggedOrderId = null;
          }
        }

        _updateBadgePosition(badgeData) {
          if (!this.chart) return;

          const series = this.chart.getSeries();
          const y = series.priceToCoordinate(badgeData.order.price);

          if (y === null || y === undefined || isNaN(y)) {
            // Hide badge if coordinate can't be calculated
            badgeData.badge.style.display = "none";
            return;
          }

          // Find badges at the same price (within 0.01 threshold)
          const samePriceBadges = [...this.badges.values()]
            .filter(
              (b) => Math.abs(b.order.price - badgeData.order.price) < 0.01
            )
            .sort((a, b) => a.order.id.localeCompare(b.order.id)); // Consistent ordering

          const index = samePriceBadges.findIndex((b) => b === badgeData);
          const offset = index * 32; // Badge height with small gap

          badgeData.badge.style.display = "flex";
          badgeData.badge.style.top = `${y - 12 + offset}px`;
          badgeData.badge.style.right = "60px";
        }

        addBadge(order) {
          if (!this.chart || !this.container) {
            console.error("❌ BadgeManager not initialized");
            return null;
          }

          // Don't show badges for filled sell/exit orders
          if (order.filled && order.side === "sell") {
            console.log(`⏭️ Skipping filled exit order: ${order.id}`);
            return null;
          }

          // Create badge element
          const badge = document.createElement("div");
          badge.className = `order-badge ${order.side}`;
          badge.id = `order-${order.id}`;

          // Build badge HTML based on order type and state
          badge.innerHTML = this._buildBadgeHTML(order);

          // Position badge
          this.container.appendChild(badge);

          // Store badge data
          const badgeData = {
            badge,
            order,
            updatePosition: () => this._updateBadgePosition(badgeData)
          };

          this.badges.set(order.id, badgeData);
          this._updateBadgePosition(badgeData);

          // Attach close button handler
          this._attachCloseHandler(badge, order);

          console.log(`📍 Added order badge: ${order.id} at $${order.price}`);
          return badge;
        }

        _buildBadgeHTML(order) {
          if (order.filled && order.side === "buy") {
            // Filled entry position - show PnL
            const pnl = this._calculatePnL(order);
            const bgColor = pnl >= 0 ? "bg-emerald-500" : "bg-red-500";
            const arrow = pnl >= 0 ? "▲" : "▼";
            const arrowColor = pnl >= 0 ? "text-emerald-400" : "text-red-400";
            const sign = "+";

            order.badge?.classList.add("filled", pnl >= 0 ? "profit" : "loss");

            return `
              <div class="${bgColor} px-3 py-1.5 rounded-l-lg text-black font-bold text-sm">${sign}${
                order.quantity
              }</div>
              <div class="bg-gray-900/95 backdrop-blur-sm px-3 py-1.5 rounded-r-lg flex items-center gap-2">
                <span class="${arrowColor} text-xs font-bold">${arrow}</span>
                <span class="text-white font-bold text-sm">$${Math.abs(
                  pnl
                ).toFixed(2)}</span>
                <button class="text-gray-400 hover:text-white ml-1 transition-colors text-lg leading-none" data-order-id="${
                  order.id
                }">×</button>
              </div>
            `;
          }

          // Pending orders
          if (order.type === "stop") {
            return `
              <div class="bg-gray-900/95 backdrop-blur-sm px-3 py-1.5 rounded-l-lg text-red-500 font-bold text-sm">Stop</div>
              <div class="bg-gray-900/95 backdrop-blur-sm px-3 py-1.5 rounded-r-lg flex items-center gap-2">
                <span class="text-red-500 font-bold text-sm">$${order.price.toFixed(
                  2
                )}</span>
                <button class="text-gray-400 hover:text-white ml-1 transition-colors text-lg leading-none" data-order-id="${
                  order.id
                }">×</button>
              </div>
            `;
          }

          if (order.side === "buy") {
            // Determine label based on order purpose
            let label = "Buy";
            let labelColor = "text-emerald-400";

            if (order.purpose === "buy-on-break") {
              label = "Break";
              labelColor = "text-blue-400";
            } else if (order.purpose === "buy-dip") {
              label = "Dip";
              labelColor = "text-yellow-400";
            } else if (order.purpose === "scale-in") {
              label = "Scale";
              labelColor = "text-purple-400";
            }

            return `
              <div class="bg-gray-900/95 backdrop-blur-sm px-3 py-1.5 rounded-l-lg ${labelColor} font-bold text-sm">${label} ${
                order.quantity
              }</div>
              <div class="bg-gray-900/95 backdrop-blur-sm px-3 py-1.5 rounded-r-lg flex items-center gap-2">
                <span class="text-white font-bold text-sm">$${order.price.toFixed(
                  2
                )}</span>
                <button class="text-gray-400 hover:text-white ml-1 transition-colors text-lg leading-none" data-order-id="${
                  order.id
                }">×</button>
              </div>
            `;
          }

          // Sell orders (targets)
          let targetIndex =
            order.targetIndex !== null && order.targetIndex !== undefined
              ? order.targetIndex + 1
              : 1;

          return `
            <div class="bg-gray-900/95 backdrop-blur-sm px-3 py-1.5 rounded-l-lg text-orange-400 font-bold text-sm">TP${targetIndex}</div>
            <div class="bg-gray-900/95 backdrop-blur-sm px-3 py-1.5 rounded-r-lg flex items-center gap-2">
              <span class="text-white font-bold text-sm">${
                order.quantity
              }@${order.price.toFixed(2)}</span>
              <button class="text-gray-400 hover:text-white ml-1 transition-colors text-lg leading-none" data-order-id="${
                order.id
              }">×</button>
            </div>
          `;
        }

        _attachCloseHandler(badge, order) {
          const closeBtn = badge.querySelector("button[data-order-id]");
          if (!closeBtn) {
            console.warn(`⚠️ No close button found for order ${order.id}`);
            return;
          }

          closeBtn.onclick = async (e) => {
            e.stopPropagation();
            console.log(`🗑️ Close button clicked for order ${order.id}`, order);

            try {
              if (order.filled) {
                // Close filled position
                await sendTradingCommand("close-now", currentSymbol, {
                  reason: "Manual close via badge"
                });
                console.log(`✅ Close position command sent for ${order.id}`);
              } else if (order.type === "stop") {
                // Cancel stop loss
                await sendTradingCommand("cancel-stop", currentSymbol, {
                  reason: "Manual cancel via UI"
                });
                console.log(`✅ Cancel stop command sent for ${order.id}`);
              } else {
                // Cancel regular order
                await sendTradingCommand("cancel-order", currentSymbol, {
                  orderId: order.id
                });
                console.log(`✅ Cancel order command sent: ${order.id}`);
              }
            } catch (error) {
              console.error(`❌ Failed to cancel/close:`, error);
            }
          };
        }

        updateBadge(orderId, updates) {
          const badgeData = this.badges.get(orderId);
          if (!badgeData) return false;

          // Update order data
          Object.assign(badgeData.order, updates);

          // Rebuild badge HTML
          badgeData.badge.innerHTML = this._buildBadgeHTML(badgeData.order);

          // Reattach close handler
          this._attachCloseHandler(badgeData.badge, badgeData.order);

          // Update position
          this._updateBadgePosition(badgeData);

          return true;
        }

        removeBadge(orderId) {
          const badgeData = this.badges.get(orderId);
          if (!badgeData) return false;

          badgeData.badge.remove();
          this.badges.delete(orderId);
          console.log(`🗑️ Removed order badge: ${orderId}`);
          return true;
        }

        clearAllBadges() {
          this.badges.forEach((badgeData) => {
            badgeData.badge.remove();
          });
          this.badges.clear();
          console.log("🗑️ Cleared all badges");
        }

        updateAllPositionPnL() {
          if (!currentPrice) return;

          let filledCount = 0;
          let conditionalCount = 0;

          this.badges.forEach((badgeData) => {
            if (badgeData.order.filled && badgeData.order.side === "buy") {
              filledCount++;
              this._updatePositionBadge(badgeData);
            } else if (
              badgeData.order.purpose === "buy-on-break" ||
              badgeData.order.purpose === "buy-dip" ||
              badgeData.order.purpose === "scale-in"
            ) {
              conditionalCount++;
              this._updateConditionalOrderBadge(badgeData);
            }
          });

          if (filledCount > 0 || conditionalCount > 0) {
            console.log(
              `🔄 Updated ${filledCount} positions and ${conditionalCount} conditional orders at $${currentPrice.toFixed(
                2
              )}`
            );
          }
        }

        _updateConditionalOrderBadge(badgeData) {
          if (!currentPrice) return;

          const order = badgeData.order;
          const priceDistance = Math.abs(currentPrice - order.price);
          const priceDistancePercent = (priceDistance / order.price) * 100;

          // Add visual indicator when price is getting close (within 2%)
          if (priceDistancePercent < 2) {
            if (
              order.purpose === "buy-on-break" &&
              currentPrice < order.price
            ) {
              // Price approaching breakout from below
              badgeData.badge.style.borderLeft = "3px solid #60a5fa"; // Blue
              badgeData.badge.style.animation =
                "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite";
            } else if (
              order.purpose === "buy-dip" &&
              currentPrice > order.price
            ) {
              // Price approaching dip from above
              badgeData.badge.style.borderLeft = "3px solid #fbbf24"; // Yellow
              badgeData.badge.style.animation =
                "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite";
            } else {
              badgeData.badge.style.borderLeft = "";
              badgeData.badge.style.animation = "";
            }
          } else {
            badgeData.badge.style.borderLeft = "";
            badgeData.badge.style.animation = "";
          }
        }

        _updatePositionBadge(badgeData) {
          const order = badgeData.order;
          const pnl = this._calculatePnL(order);
          const pnlClass = pnl >= 0 ? "profit" : "loss";

          // Update badge classes
          badgeData.badge.className = `order-badge ${order.side} filled ${pnlClass}`;

          // Rebuild HTML with updated PnL
          badgeData.badge.innerHTML = this._buildBadgeHTML(order);

          // Reattach close handler
          this._attachCloseHandler(badgeData.badge, order);
        }

        _calculatePnL(order) {
          if (!order.filled || !currentPrice) return 0;

          const priceDiff =
            order.side === "buy"
              ? currentPrice - order.fillPrice
              : order.fillPrice - currentPrice;

          return priceDiff * order.quantity;
        }

        getBadge(orderId) {
          return this.badges.get(orderId);
        }

        hasBadge(orderId) {
          return this.badges.has(orderId);
        }

        destroy() {
          console.log("🗑️ Destroying BadgeManager...");

          // Clear all badges and DOM elements
          this.clearAllBadges();

          // Clean up event listeners
          if (this._cleanup) {
            this._cleanup();
            this._cleanup = null;
          }

          // Clear state
          this.chart = null;
          this.container = null;
          this.isDragging = false;
          this.draggedOrderId = null;
          this.dragStartY = 0;
          this.dragStartPrice = 0;
          this.onMoveOrder = null;
          this.onCancelOrder = null;
          this.onClosePosition = null;

          console.log("✅ BadgeManager destroyed");
        }
      }

      // Create global badge manager instance
      const badgeManager = new BadgeManager();

      // Replay mode configuration
      const REPLAY_MODE = import.meta.env.VITE_REPLAY_MODE === "true";
      const REPLAY_DATE = import.meta.env.VITE_REPLAY_DATE; // Format: DD/MM/YYYY

      // Convert replay date from DD/MM/YYYY to YYYY-MM-DD
      function getReplayDateParam() {
        if (!REPLAY_MODE || !REPLAY_DATE) return "";

        const parts = REPLAY_DATE.split("/");
        if (parts.length !== 3) return "";

        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      }

      // Watchlist symbols - will be loaded from API
      let watchlist = [];

      // Track symbols with active trades
      const symbolsWithActiveTrades = new Set();

      // Hydrate watchlist "active trade" ✓ indicators by fetching open trades across all symbols.
      // This is used on refresh so the watchlist reflects reality before you click into a symbol.
      async function hydrateWatchlistActiveTrades() {
        try {
          const response = await apiFetch(`${API_BASE_URL}/api/trades`);
          if (!response.ok) {
            const bodyText = await response.text().catch(() => "");
            console.warn(
              `⚠️ Failed to load open trades for watchlist: ${response.status}`,
              bodyText
            );
            return;
          }

          const result = await response.json();
          const trades = result.trades || result;

          symbolsWithActiveTrades.clear();
          (trades || []).forEach((t) => {
            const sym = String(t.symbol || "").toUpperCase();
            if (sym) symbolsWithActiveTrades.add(sym);
          });

          renderWatchlist();
        } catch (error) {
          console.error("❌ Error hydrating watchlist active trades:", error);
        }
      }

      // Trading session ID - unique per session
      const sessionId = `session_${Date.now()}_${Math.random()
        .toString(36)
        .substring(7)}`;

      // Track in-flight commands to prevent duplicates
      const inFlightCommands = new Set();

      // Send trading command to API
      async function sendTradingCommand(action, symbol, params = {}) {
        // 🔧 CRITICAL FIX: Prevent duplicate commands
        const commandKey = `${action}_${symbol}`;
        if (inFlightCommands.has(commandKey)) {
          console.warn(`⚠️ Command already in flight: ${commandKey}`);
          throw new Error(`Command already in progress: ${action}`);
        }

        // Mark command as in-flight
        inFlightCommands.add(commandKey);
        console.log(`🔒 Command locked: ${commandKey}`);
        // Check WebSocket connection - wait for reconnection if needed
        if (!wsConnected) {
          console.warn(
            "⚠️ WebSocket disconnected, waiting for auto-reconnect..."
          );

          // Wait up to 10 seconds for reconnection
          const maxWaitTime = 10000;
          const startTime = Date.now();

          while (!wsConnected && Date.now() - startTime < maxWaitTime) {
            await new Promise((resolve) => setTimeout(resolve, 500));

            if (wsConnected) {
              console.log("✅ WebSocket reconnected, proceeding with command");
              break;
            }
          }

          // If still not connected after waiting, throw error
          if (!wsConnected) {
            throw new Error(
              "Cannot send command - WebSocket is disconnected. Please check your connection and try again."
            );
          }
        }

        // Build command message (server generates commandId and timestamp)
        const command = {
          type: "command",
          action: action,
          symbol: symbol,
          sessionId: sessionId,
          params: params
        };

        console.log(`📤 Sending command via WebSocket:`, command);

        // Send via WebSocket
        websocketService.send(command);

        // Return promise that resolves when we get command_ack
        // Server will send back commandId in the response
        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            // 🔧 FIX: Unlock command on timeout
            inFlightCommands.delete(commandKey);
            console.log(`🔓 Command unlocked (timeout): ${commandKey}`);
            reject(new Error(`Command timeout after 5s: ${action}`));
          }, 5000);

          // Store resolver with action/symbol to match response
          // Server returns commandId in command_ack
          const requestKey = `${action}_${symbol}_${Date.now()}`;
          pendingCommands.set(requestKey, {
            resolve: (result) => {
              // 🔧 FIX: Unlock command on success
              inFlightCommands.delete(commandKey);
              console.log(`🔓 Command unlocked (success): ${commandKey}`);
              resolve(result);
            },
            reject: (error) => {
              // 🔧 FIX: Unlock command on error
              inFlightCommands.delete(commandKey);
              console.log(`🔓 Command unlocked (error): ${commandKey}`);
              reject(error);
            },
            timeout,
            action,
            symbol
          });
        });
      }

      // Handle order updates from WebSocket
      function handleOrderUpdate(orderData) {
        console.log("🔔 Processing order update:", orderData);
        if (orderData.purpose === "target" || orderData.side === "sell") {
          console.log(
            "📊 Target order data - targetIndex:",
            orderData.targetIndex,
            "purpose:",
            orderData.purpose
          );
        }

        // Check if order is cancelled or replaced (handle different formats)
        const isCancelled =
          orderData.status?.toLowerCase() === "cancelled" ||
          orderData.status?.toLowerCase() === "canceled" ||
          orderData.status?.toLowerCase() === "replaced";

        // If cancelled or replaced, just remove and return
        if (isCancelled) {
          badgeManager.removeBadge(orderData.orderId);
          console.log(`🗑️ Order ${orderData.status}: ${orderData.orderId}`);
          return;
        }

        // Convert API order format to badge format
        const order = {
          id: orderData.orderId,
          tradeId: orderData.tradeId,
          symbol: orderData.symbol,
          side: orderData.side?.toLowerCase() || "buy", // "BUY" -> "buy"
          type: orderData.purpose === "stop" ? "stop" : "limit",
          purpose: orderData.purpose, // "buy-dip", "buy-on-break", "entry", "target", "stop"
          quantity: orderData.quantity || orderData.filledQty || 0,
          price: orderData.limitPrice || orderData.avgFillPrice || 0,
          filled: orderData.status === "filled",
          fillPrice: orderData.avgFillPrice,
          fillTime: orderData.filledAt
            ? new Date(orderData.filledAt).getTime()
            : null,
          targetIndex: orderData.targetIndex // Pass through targetIndex for target orders
        };

        // Check if order badge already exists
        const existingBadge = badgeManager.getBadge(order.id);

        if (existingBadge) {
          // Update existing badge
          if (order.filled && !existingBadge.order.filled) {
            // Order just got filled
            if (order.side === "buy") {
              // Entry order filled - update the badge to show position with PnL
              badgeManager.updateBadge(order.id, {
                filled: true,
                fillPrice: order.fillPrice,
                fillTime: order.fillTime
              });
              console.log(`✅ Entry order filled: ${order.id}`);
            } else {
              // Exit/target order filled - remove the badge
              badgeManager.removeBadge(order.id);
              console.log(`✅ Exit order filled, badge removed: ${order.id}`);
            }
          } else if (!order.filled) {
            // Update pending order (price/quantity changed)
            badgeManager.updateBadge(order.id, {
              price: order.price,
              quantity: order.quantity
            });
            console.log(`📝 Order updated: ${order.id} @ $${order.price}`);
          }
        } else {
          // New order - add badge (but not for filled sell orders)
          if (order.filled && order.side === "sell") {
            console.log(`⏭️ Skipping filled exit order: ${order.id}`);
          } else {
            badgeManager.addBadge(order);
            console.log(`📍 New order added: ${order.id} @ $${order.price}`);
          }
        }
      }

      // Load active symbols from API to populate watchlist
      async function loadActiveSymbols() {
        try {
          console.log("📋 Loading active symbols for watchlist...");
          const response = await fetch(`${API_BASE_URL}/api/symbols/active`);

          if (!response.ok) {
            console.error(
              "❌ Failed to load active symbols:",
              response.status,
              response.statusText
            );
            watchlist = [];
            return;
          }

          const result = await response.json();
          console.log("📊 Active symbols API response:", result);

          const symbols = result.results || result.symbols || result;

          // Convert to watchlist format
          watchlist = symbols.map((item) => ({
            symbol: item.symbol,
            price: item.lastBar?.close || item.price || 0,
            change: 0, // Will be updated by quote updates
            initialPrice: item.lastBar?.close || item.price || 0 // Track initial price for change calculation
          }));

          console.log(
            `✅ Loaded ${watchlist.length} active symbols:`,
            watchlist.map((w) => w.symbol)
          );
        } catch (error) {
          console.error("❌ Error loading active symbols:", error);
          watchlist = [];
        }
      }

      // Function to load symbol data
      function updateCurrentBarFromTrade(trade) {
        // Safety check: only update if chart exists and matches current symbol
        if (!chart || !currentSymbol || trade.symbol !== currentSymbol) {
          return;
        }

        const price = parseFloat(trade.price);
        const size = parseFloat(trade.size);
        const tradeTime = new Date(trade.timestamp).getTime();
        const barMinute = Math.floor(tradeTime / 60000) * 60000;

        // Initialize current bar if needed
        if (!currentBar) {
          currentBar = {
            symbol: currentSymbol,
            timestamp: barMinute,
            open: price,
            high: price,
            low: price,
            close: price,
            volume: size,
            vwap: price,
            tradeCount: 1,
            totalValue: price * size
          };
          console.log(
            `🕐 Started new bar at ${new Date(barMinute).toISOString()}`
          );
          return;
        }

        const currentBarMinute =
          Math.floor(currentBar.timestamp / 60000) * 60000;

        // Check if we need to complete the current bar and start a new one
        if (barMinute > currentBarMinute) {
          // Complete the current bar
          const completedBar = {
            symbol: currentBar.symbol,
            timestamp: currentBar.timestamp,
            open: currentBar.open,
            high: currentBar.high,
            low: currentBar.low,
            close: currentBar.close,
            volume: currentBar.volume,
            vwap: currentBar.vwap,
            tradeCount: currentBar.tradeCount
          };

          console.log(`✅ Bar completed:`, {
            time: new Date(currentBar.timestamp).toISOString(),
            open: currentBar.open.toFixed(2),
            high: currentBar.high.toFixed(2),
            low: currentBar.low.toFixed(2),
            close: currentBar.close.toFixed(2)
          });

          // Add to chart data
          chartData.push(completedBar);

          // Update chart with new bar (addDataPoint handles timestamp conversion)
          if (chart) {
            try {
              chart.addDataPoint(completedBar);
            } catch (error) {
              // Silently ignore chart update errors during transitions
              if (error.message !== "Object is disposed") {
                console.warn("⚠️ Error adding completed bar to chart:", error);
              }
            }
          }

          // Start new bar
          currentBar = {
            symbol: currentSymbol,
            timestamp: barMinute,
            open: price,
            high: price,
            low: price,
            close: price,
            volume: size,
            vwap: price,
            tradeCount: 1,
            totalValue: price * size
          };

          console.log(
            `🕐 Started new bar at ${new Date(barMinute).toISOString()}`
          );
        } else {
          // Update current bar with new trade
          currentBar.high = Math.max(currentBar.high, price);
          currentBar.low = Math.min(currentBar.low, price);
          currentBar.close = price;
          currentBar.volume += size;
          currentBar.tradeCount++;
          currentBar.totalValue += price * size;
          currentBar.vwap = currentBar.totalValue / currentBar.volume;

          // Update chart with current (incomplete) bar
          if (chart) {
            try {
              const currentBarForChart = {
                symbol: currentBar.symbol,
                timestamp: currentBar.timestamp,
                open: currentBar.open,
                high: currentBar.high,
                low: currentBar.low,
                close: currentBar.close,
                volume: currentBar.volume,
                vwap: currentBar.vwap,
                tradeCount: currentBar.tradeCount
              };
              chart.addDataPoint(currentBarForChart);
            } catch (error) {
              // Silently ignore chart update errors during transitions
              if (error.message !== "Object is disposed") {
                console.warn("⚠️ Error updating current bar on chart:", error);
              }
            }
          }
        }
      }

      function updatePriceFromQuote(quote) {
        if (!quote.bid || !quote.ask) return;

        // Safety check: only update if chart exists and matches current symbol
        if (!chart || !currentSymbol || quote.symbol !== currentSymbol) {
          return;
        }

        const mid = (parseFloat(quote.ask) + parseFloat(quote.bid)) / 2;
        currentPrice = mid; // Update global current price for PnL calculations

        // Update PnL for all filled positions
        badgeManager.updateAllPositionPnL();

        // Update current price display
        const currentPriceEl = document.getElementById("currentPrice");
        if (currentPriceEl) {
          currentPriceEl.textContent = `$${mid.toFixed(2)}`;
        }

        // Update price change badge (compare to first bar's close)
        if (chartData.length > 0) {
          const firstClose = chartData[0].close;
          const change = mid - firstClose;
          const changePercent = (change / firstClose) * 100;
          // IMPORTANT: never overwrite badge.innerHTML/textContent.
          // The header expects #priceChangeBadge to contain the arrow <span> and the #changeText <span>.
          // If we clobber it, symbol switching can crash in updateHeaderInfo().
          updatePriceChangeBadge(change, changePercent);
        }

        // Update the chart - check if we need a new bar or update existing
        if (chart && chartData.length > 0) {
          try {
            const now = new Date(quote.timestamp || Date.now()).getTime();
            const currentMinute = Math.floor(now / 60000) * 60000;

            const lastBar = chartData[chartData.length - 1];
            const lastBarMinute = Math.floor(lastBar.timestamp / 60000) * 60000;

            // CRITICAL: Ignore quotes older than the last bar
            if (currentMinute < lastBarMinute) {
              console.log(
                `⚠️ Ignoring old quote: ${new Date(
                  currentMinute
                ).toISOString()} < last bar ${new Date(
                  lastBarMinute
                ).toISOString()}`
              );
              return;
            }

            const series = chart.getSeries();
            if (!series) {
              console.warn(
                "⚠️ Chart series not available, skipping quote update"
              );
              return;
            }

            if (currentMinute === lastBarMinute) {
              // Same minute - update existing bar
              lastBar.close = mid;
              lastBar.high = Math.max(lastBar.high, mid);
              lastBar.low = Math.min(lastBar.low, mid);

              // Update the series (time in seconds)
              series.update({
                time: Math.floor(Number(lastBar.timestamp) / 1000),
                open: lastBar.open,
                high: lastBar.high,
                low: lastBar.low,
                close: lastBar.close
              });
            } else {
              // New minute - create new bar
              const newBar = {
                symbol: currentSymbol,
                timestamp: currentMinute,
                open: mid,
                high: mid,
                low: mid,
                close: mid,
                volume: 0
              };

              chartData.push(newBar);

              // Add new bar to series (time in seconds)
              series.update({
                time: Math.floor(Number(currentMinute) / 1000),
                open: mid,
                high: mid,
                low: mid,
                close: mid
              });
            }
          } catch (error) {
            // Silently ignore chart update errors during transitions
            if (error.message !== "Object is disposed") {
              console.warn("⚠️ Error updating chart from quote:", error);
            }
          }
        }
      }

      // Ensure the price-change badge has the expected internal structure:
      // <span>▲/▼</span><span id="changeText">...</span>
      function ensurePriceChangeBadgeStructure() {
        const badge = document.getElementById("priceChangeBadge");
        if (!badge) return null;

        let arrowSpan = badge.querySelector("span:first-child");
        let changeText = document.getElementById("changeText");

        // If structure was clobbered (e.g., by setting textContent), rebuild it.
        if (!arrowSpan || !changeText || changeText.parentElement !== badge) {
          badge.innerHTML = `
            <span>▲</span>
            <span id="changeText"></span>
          `;
          arrowSpan = badge.querySelector("span:first-child");
          changeText = document.getElementById("changeText");
        }

        return { badge, arrowSpan, changeText };
      }

      function updatePriceChangeBadge(change, changePercent) {
        const parts = ensurePriceChangeBadgeStructure();
        if (!parts) return;

        const { badge, arrowSpan, changeText } = parts;
        const isPositive = change >= 0;

        badge.className = `price-change-badge ${isPositive ? "positive" : "negative"}`;
        if (arrowSpan) arrowSpan.textContent = isPositive ? "▲" : "▼";
        if (changeText) {
          changeText.textContent = `$${Math.abs(change).toFixed(2)} (${Math.abs(changePercent).toFixed(2)}%)`;
        }
      }

      // Real-time quote updates are now handled via WebSocket
      // Subscribe to quotes:${symbol} channel for live price updates
      // The handleWebSocketMessage function processes quote messages

      // Watchlist functions
      function renderWatchlist() {
        const container = document.getElementById("watchlistItems");
        const countEl = document.getElementById("watchlistCount");

        countEl.textContent = watchlist.length;

        container.innerHTML = watchlist
          .map((item, index) => {
            const isPositive = item.change >= 0;
            const changeClass = isPositive ? "positive" : "negative";
            const arrow = isPositive ? "▲" : "▼";
            const isActive = item.symbol === currentSymbol;

            const hasActiveTrade = symbolsWithActiveTrades.has(item.symbol);

            return `
            <div class="watchlist-item ${
              isActive ? "active" : ""
            }" data-symbol="${item.symbol}">
              <div class="watchlist-item-index">${index + 1}</div>
              <div class="watchlist-item-symbol">
                ${item.symbol}
                ${
                  hasActiveTrade
                    ? '<span class="trade-indicator" title="Active trade">✓</span>'
                    : ""
                }
              </div>
              <div class="watchlist-item-price">$${item.price.toFixed(
                item.price < 1 ? 4 : 2
              )}</div>
              <div class="watchlist-item-change ${changeClass}">
                ${arrow} $${Math.abs(item.change).toFixed(
                  item.price < 1 ? 4 : 2
                )}
              </div>
              <div class="watchlist-item-menu" data-symbol="${item.symbol}">
                ⋮
                <div class="watchlist-dropdown" data-symbol="${item.symbol}">
                  <div class="watchlist-dropdown-item remove" data-symbol="${
                    item.symbol
                  }">Remove</div>
                </div>
              </div>
            </div>
          `;
          })
          .join("");

        // Add click handlers for items
        container.querySelectorAll(".watchlist-item").forEach((item) => {
          item.addEventListener("click", async (e) => {
            // Don't trigger if clicking menu or dropdown
            if (
              e.target.classList.contains("watchlist-item-menu") ||
              e.target.classList.contains("watchlist-dropdown") ||
              e.target.classList.contains("watchlist-dropdown-item") ||
              e.target.closest(".watchlist-item-menu")
            ) {
              return;
            }

            const symbol = item.getAttribute("data-symbol");

            // Don't reload if already active
            if (symbol === currentSymbol) {
              return;
            }

            // Add loading state to clicked item
            item.classList.add("loading");

            // Load symbol
            await loadSymbol(symbol);

            // Remove loading state
            item.classList.remove("loading");
          });
        });

        // Add click handlers for menu buttons
        container.querySelectorAll(".watchlist-item-menu").forEach((menu) => {
          menu.addEventListener("click", (e) => {
            e.stopPropagation();
            const dropdown = menu.querySelector(".watchlist-dropdown");

            // Close all other dropdowns
            document
              .querySelectorAll(".watchlist-dropdown.active")
              .forEach((d) => {
                if (d !== dropdown) {
                  d.classList.remove("active");
                }
              });

            // Toggle current dropdown
            dropdown.classList.toggle("active");
          });
        });

        // Add click handlers for dropdown items
        container
          .querySelectorAll(".watchlist-dropdown-item")
          .forEach((item) => {
            item.addEventListener("click", (e) => {
              e.stopPropagation();
              const symbol = item.getAttribute("data-symbol");

              // Close dropdown
              item.closest(".watchlist-dropdown").classList.remove("active");

              if (item.classList.contains("remove")) {
                // Remove from watchlist
                removeFromWatchlist(symbol);
              }
            });
          });

        // Close dropdown when clicking outside
        document.addEventListener("click", (e) => {
          if (!e.target.closest(".watchlist-item-menu")) {
            document
              .querySelectorAll(".watchlist-dropdown.active")
              .forEach((d) => {
                d.classList.remove("active");
              });
          }
        });
      }

      async function removeFromWatchlist(symbol) {
        try {
          // Unsubscribe from WebSocket channels for this symbol
          if (wsConnected && websocketService.isAuthenticated()) {
            websocketService.unsubscribe(`trade:${symbol}`);
            websocketService.unsubscribe(`orders:${symbol}`);
            websocketService.unsubscribe(`quotes:${symbol}`);
            websocketService.unsubscribe(`market:${symbol}`);
            websocketService.unsubscribe(`indicators:${symbol}`);
            console.log(`🔕 Unsubscribed from ${symbol} channels`);
          }

          // Mark symbol as not actively trading via API
          console.log(`📝 Marking ${symbol} as not actively trading...`);
          const response = await fetch(
            `${API_BASE_URL}/api/symbols/${symbol}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json"
              },
              body: JSON.stringify({
                isActivelyTrading: false
              })
            }
          );

          if (!response.ok) {
            console.error(
              "❌ Failed to mark symbol as inactive:",
              response.status
            );
          }

          console.log(`✅ Marked ${symbol} as not actively trading`);
        } catch (error) {
          console.error("❌ Error removing symbol from watchlist:", error);
        }

        // Add animation class to the item
        const item = document.querySelector(
          `.watchlist-item[data-symbol="${symbol}"]`
        );
        if (item) {
          item.classList.add("removing");
        }

        // Wait for animation to complete, then remove
        setTimeout(() => {
          // Unsubscribe from quotes for this symbol
          if (wsConnected && websocketService.isAuthenticated()) {
            websocketService.unsubscribe(`quotes:${symbol}`);
            console.log(`🔕 Unsubscribed from quotes for ${symbol}`);
          }

          // Remove from array
          const index = watchlist.findIndex((item) => item.symbol === symbol);
          if (index !== -1) {
            watchlist.splice(index, 1);
            console.log(`🗑️ Removed ${symbol} from watchlist`);
            renderWatchlist();

            // If removing the currently active symbol, load another symbol if available
            if (symbol === currentSymbol) {
              if (watchlist.length > 0) {
                loadSymbol(watchlist[0].symbol);
              } else {
                console.log("📋 Watchlist is now empty");

                // Clear the chart
                if (chart) {
                  chart.remove();
                  chart = null;
                }

                // Clear current symbol
                currentSymbol = null;

                // Show search dialog to add a symbol
                openSearchDialog();
              }
            }
          }
        }, 300);
      }

      // Popular equities for search
      // Popular equities - loaded dynamically or fallback
      let popularEquities = [
        { symbol: "NVDA", name: "NVIDIA" },
        { symbol: "TSLA", name: "Tesla" },
        { symbol: "AAPL", name: "Apple" },
        { symbol: "AMZN", name: "Amazon" },
        { symbol: "MSFT", name: "Microsoft" },
        { symbol: "GOOGL", name: "Alphabet" },
        { symbol: "META", name: "Meta" },
        { symbol: "AMD", name: "Advanced Micro Devices" },
        { symbol: "NFLX", name: "Netflix" },
        { symbol: "INTC", name: "Intel" }
      ];

      // Search symbols via API
      async function searchSymbols(query) {
        try {
          console.log(`🔍 Searching for: ${query}`);
          const response = await fetch(
            `${API_BASE_URL}/api/symbols/search?query=${encodeURIComponent(
              query
            )}&limit=20`
          );

          if (!response.ok) {
            console.warn("⚠️ Search API failed, using local filter");
            return popularEquities.filter(
              (item) =>
                item.symbol.toUpperCase().includes(query.toUpperCase()) ||
                item.name.toUpperCase().includes(query.toUpperCase())
            );
          }

          const result = await response.json();
          const results = result.results || result.symbols || [];

          // Convert to expected format
          return results.map((item) => ({
            symbol: item.symbol,
            name: item.name || item.symbol
          }));
        } catch (error) {
          console.error("❌ Error searching symbols:", error);
          // Fallback to local filtering
          return popularEquities.filter(
            (item) =>
              item.symbol.toUpperCase().includes(query.toUpperCase()) ||
              item.name.toUpperCase().includes(query.toUpperCase())
          );
        }
      }

      // Update a single watchlist item without re-rendering entire list
      function updateWatchlistItem(symbol, price, change) {
        const item = document.querySelector(
          `.watchlist-item[data-symbol="${symbol}"]`
        );
        if (!item) return;

        const priceEl = item.querySelector(".watchlist-item-price");
        const changeEl = item.querySelector(".watchlist-item-change");

        if (priceEl) {
          priceEl.textContent = `$${price.toFixed(price < 1 ? 4 : 2)}`;
        }

        if (changeEl) {
          const isPositive = change >= 0;
          const arrow = isPositive ? "▲" : "▼";
          changeEl.className = `watchlist-item-change ${
            isPositive ? "positive" : "negative"
          }`;
          changeEl.textContent = `${arrow} $${Math.abs(change).toFixed(
            price < 1 ? 4 : 2
          )}`;
        }
      }

      // Update trade indicator (checkmark) for a symbol
      function updateWatchlistTradeIndicator(symbol, hasActiveTrade) {
        const item = document.querySelector(
          `.watchlist-item[data-symbol="${symbol}"]`
        );
        if (!item) return;

        const symbolEl = item.querySelector(".watchlist-item-symbol");
        if (!symbolEl) return;

        // Remove existing indicator
        const existingIndicator = symbolEl.querySelector(".trade-indicator");
        if (existingIndicator) {
          existingIndicator.remove();
        }

        // Add indicator if has active trade
        if (hasActiveTrade) {
          const indicator = document.createElement("span");
          indicator.className = "trade-indicator";
          indicator.textContent = "✓";
          indicator.title = "Active trade";
          symbolEl.appendChild(indicator);
        }
      }

      function setupWatchlistControls() {
        // Add symbol button - opens search dialog
        document
          .getElementById("addSymbolBtn")
          .addEventListener("click", () => {
            openSearchDialog();
          });
      }

      function openSearchDialog() {
        const overlay = document.getElementById("searchDialogOverlay");
        const input = document.getElementById("searchDialogInput");

        overlay.classList.add("active");
        input.value = "";
        input.focus();

        // Show popular equities by default
        renderSearchResults(popularEquities, "Popular equities");
      }

      function closeSearchDialog() {
        const overlay = document.getElementById("searchDialogOverlay");
        overlay.classList.remove("active");
      }

      function renderSearchResults(results, title = "Results") {
        const content = document.getElementById("searchDialogContent");

        if (results.length === 0) {
          content.innerHTML =
            '<div class="search-no-results">No symbols found</div>';
          return;
        }

        content.innerHTML = `
          <div class="search-section-title">${title}</div>
          <div class="search-results">
            ${results
              .map(
                (item) => `
              <div class="search-result-item" data-symbol="${item.symbol}">
                <div class="search-result-info">
                  <div class="search-result-symbol">${item.symbol}</div>
                  <div class="search-result-name">${item.name}</div>
                </div>
                <div class="search-result-add">+</div>
              </div>
            `
              )
              .join("")}
          </div>
        `;

        // Add click handlers
        content.querySelectorAll(".search-result-item").forEach((item) => {
          item.addEventListener("click", () => {
            const symbol = item.getAttribute("data-symbol");
            const name = item.querySelector(".search-result-name").textContent;
            addSymbolToWatchlist(symbol, name);
          });
        });
      }

      async function addSymbolToWatchlist(symbol, name) {
        // Check if already exists
        if (watchlist.find((item) => item.symbol === symbol)) {
          alert(`${symbol} is already in the watchlist`);
          return;
        }

        try {
          // Mark symbol as actively trading via API
          console.log(`📝 Marking ${symbol} as actively trading...`);
          const response = await fetch(
            `${API_BASE_URL}/api/symbols/${symbol}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json"
              },
              body: JSON.stringify({
                isActivelyTrading: true,
                name: name || symbol
              })
            }
          );

          if (!response.ok) {
            console.error(
              "❌ Failed to mark symbol as active:",
              response.status
            );
            alert(`Failed to add ${symbol} to watchlist`);
            return;
          }

          console.log(`✅ Marked ${symbol} as actively trading`);

          // Add to local watchlist
          watchlist.push({
            symbol: symbol,
            price: 0,
            change: 0,
            initialPrice: 0 // Will be set by first quote
          });

          console.log(`✅ Added ${symbol} to watchlist`);

          // Subscribe to quotes for real-time price updates in watchlist
          if (wsConnected && websocketService.isAuthenticated()) {
            websocketService.subscribe(`quotes:${symbol}`);
            console.log(`✅ Subscribed to quotes for ${symbol}`);
          }

          renderWatchlist();
          closeSearchDialog();

          // Load the newly added symbol
          loadSymbol(symbol);
        } catch (error) {
          console.error("❌ Error adding symbol to watchlist:", error);
          alert(`Failed to add ${symbol} to watchlist`);
        }
      }

      // Config state variables
      let configState = {
        currentPreset: "TRIMMER",
        runnerModeEnabled: false,
        automatedSignalsEnabled: false,
        accountSize: null,
        riskPerTradePercent: null,
        maxPositionValuePercent: null
      };

      // Helper function to format currency
      function formatCurrency(value) {
        return "$" + value.toLocaleString("en-US");
      }

      // Function to load symbol configuration and update UI
      async function loadSymbolConfig(symbol) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/symbols/${symbol}`);

          if (!response.ok) {
            console.warn(`Failed to fetch config for ${symbol}`);
            return;
          }

          const symbolData = await response.json();

          // Update target preset UI
          configState.currentPreset = symbolData.targetPreset || "TRIMMER";
          document.querySelectorAll(".config-btn-preset").forEach((btn) => {
            btn.classList.toggle(
              "active",
              btn.dataset.preset === configState.currentPreset
            );
          });

          // Update runner mode UI
          configState.runnerModeEnabled = symbolData.runnerModeEnabled || false;
          const runnerToggle = document.getElementById("runnerModeToggle");
          runnerToggle.classList.toggle(
            "active",
            configState.runnerModeEnabled
          );
          runnerToggle.querySelector(".runner-status").textContent =
            configState.runnerModeEnabled ? "Enabled" : "Disabled";

          // Update automated signals UI
          configState.automatedSignalsEnabled =
            symbolData.automatedSignalsEnabled || false;
          configState.accountSize =
            symbolData.accountSize != null ? symbolData.accountSize : 50000;
          configState.riskPerTradePercent =
            symbolData.riskPerTradePercent != null
              ? symbolData.riskPerTradePercent
              : 1;
          configState.maxPositionValuePercent =
            symbolData.maxPositionValuePercent != null
              ? symbolData.maxPositionValuePercent
              : 10;

          const autoToggle = document.getElementById("automatedSignalsToggle");
          autoToggle.classList.toggle(
            "active",
            configState.automatedSignalsEnabled
          );
          autoToggle.querySelector(".auto-signals-status").textContent =
            configState.automatedSignalsEnabled ? "Enabled" : "Disabled";

          // Update slider displays immediately (even if dialog is closed)
          const accountSizeSlider = document.getElementById(
            "autoSignalsAccountSize"
          );
          const riskPercentSlider = document.getElementById(
            "autoSignalsRiskPercent"
          );
          const maxPositionSlider = document.getElementById(
            "autoSignalsMaxPosition"
          );

          if (accountSizeSlider) {
            accountSizeSlider.value = configState.accountSize;
            document.getElementById("autoSignalsAccountSizeValue").textContent =
              formatCurrency(configState.accountSize);
          }

          if (riskPercentSlider) {
            riskPercentSlider.value = configState.riskPerTradePercent;
            const riskDollar =
              (configState.accountSize * configState.riskPerTradePercent) / 100;
            document.getElementById("autoSignalsRiskPercentValue").textContent =
              configState.riskPerTradePercent +
              "% (" +
              formatCurrency(riskDollar) +
              ")";
          }

          if (maxPositionSlider) {
            maxPositionSlider.value = configState.maxPositionValuePercent;
            const maxPosDollar =
              (configState.accountSize * configState.maxPositionValuePercent) /
              100;
            document.getElementById("autoSignalsMaxPositionValue").textContent =
              configState.maxPositionValuePercent +
              "% (" +
              formatCurrency(maxPosDollar) +
              ")";
          }

          console.log(`✅ Loaded config for ${symbol}:`, {
            targetPreset: configState.currentPreset,
            runnerEnabled: configState.runnerModeEnabled,
            autoSignalsEnabled: configState.automatedSignalsEnabled,
            accountSize: configState.accountSize,
            riskPerTradePercent: configState.riskPerTradePercent,
            maxPositionValuePercent: configState.maxPositionValuePercent
          });
        } catch (error) {
          console.error(`❌ Error loading config for ${symbol}:`, error);
        }
      }

      function setupConfigPanel() {
        // Target preset buttons
        const presetButtons = document.querySelectorAll(".config-btn-preset");

        presetButtons.forEach((button) => {
          button.addEventListener("click", async () => {
            const preset = button.dataset.preset;

            try {
              console.log(`📊 Setting target preset to: ${preset}`);

              await sendTradingCommand("set-target-preset", currentSymbol, {
                targetPreset: preset
              });

              // Update UI
              presetButtons.forEach((btn) => btn.classList.remove("active"));
              button.classList.add("active");
              configState.currentPreset = preset;

              console.log(`✅ Target preset updated to: ${preset}`);
            } catch (error) {
              console.error(`❌ Failed to set target preset:`, error);
              alert(`Failed to set target preset: ${error.message}`);
            }
          });
        });

        // Runner mode toggle
        const runnerModeToggle = document.getElementById("runnerModeToggle");

        runnerModeToggle.addEventListener("click", async () => {
          const newState = !configState.runnerModeEnabled;

          try {
            console.log(
              `🏃 ${newState ? "Enabling" : "Disabling"} runner mode`
            );

            await sendTradingCommand("enable-runner-mode", currentSymbol, {
              enableRunnerMode: newState
            });

            // Update state and UI
            configState.runnerModeEnabled = newState;
            runnerModeToggle.classList.toggle("active", newState);
            runnerModeToggle.querySelector(".runner-status").textContent =
              newState ? "Enabled" : "Disabled";

            console.log(`✅ Runner mode ${newState ? "enabled" : "disabled"}`);
          } catch (error) {
            console.error(`❌ Failed to toggle runner mode:`, error);
            alert(`Failed to toggle runner mode: ${error.message}`);
          }
        });

        // Automated signals button - open dialog
        const automatedSignalsToggle = document.getElementById(
          "automatedSignalsToggle"
        );

        automatedSignalsToggle.addEventListener("click", () => {
          openAutoSignalsDialog();
        });
      }

      // Automated Signals Dialog functions
      function openAutoSignalsDialog() {
        const overlay = document.getElementById("autoSignalsDialogOverlay");
        const toggleSwitch = document.getElementById("autoSignalsToggleSwitch");

        // Update toggle switch to match current state
        if (configState.automatedSignalsEnabled) {
          toggleSwitch.classList.add("active");
        } else {
          toggleSwitch.classList.remove("active");
        }

        // Sliders are already synced from loadSymbolConfig(), no need to update them again

        overlay.classList.add("active");
      }

      function closeAutoSignalsDialog() {
        const overlay = document.getElementById("autoSignalsDialogOverlay");
        overlay.classList.remove("active");
      }

      function setupAutoSignalsDialog() {
        const overlay = document.getElementById("autoSignalsDialogOverlay");
        const closeBtn = document.getElementById("autoSignalsDialogClose");
        const cancelBtn = document.getElementById("autoSignalsCancelBtn");
        const saveBtn = document.getElementById("autoSignalsSaveBtn");
        const toggleSwitch = document.getElementById("autoSignalsToggleSwitch");

        // Slider elements
        const accountSizeSlider = document.getElementById(
          "autoSignalsAccountSize"
        );
        const riskPercentSlider = document.getElementById(
          "autoSignalsRiskPercent"
        );
        const maxPositionSlider = document.getElementById(
          "autoSignalsMaxPosition"
        );

        // Value display elements
        const accountSizeValue = document.getElementById(
          "autoSignalsAccountSizeValue"
        );
        const riskPercentValue = document.getElementById(
          "autoSignalsRiskPercentValue"
        );
        const maxPositionValue = document.getElementById(
          "autoSignalsMaxPositionValue"
        );

        // Slider input handlers - update displays in real-time
        accountSizeSlider.addEventListener("input", (e) => {
          const accountSize = parseInt(e.target.value);
          accountSizeValue.textContent = formatCurrency(accountSize);

          // Update risk and max position dollar values when account size changes
          const riskPercent = parseFloat(riskPercentSlider.value);
          const maxPosPercent = parseFloat(maxPositionSlider.value);

          const riskDollar = (accountSize * riskPercent) / 100;
          const maxPosDollar = (accountSize * maxPosPercent) / 100;

          riskPercentValue.textContent =
            riskPercent + "% (" + formatCurrency(riskDollar) + ")";
          maxPositionValue.textContent =
            maxPosPercent + "% (" + formatCurrency(maxPosDollar) + ")";
        });

        riskPercentSlider.addEventListener("input", (e) => {
          const riskPercent = parseFloat(e.target.value);
          const accountSize = parseInt(accountSizeSlider.value);
          const riskDollar = (accountSize * riskPercent) / 100;
          riskPercentValue.textContent =
            riskPercent + "% (" + formatCurrency(riskDollar) + ")";
        });

        maxPositionSlider.addEventListener("input", (e) => {
          const maxPosPercent = parseFloat(e.target.value);
          const accountSize = parseInt(accountSizeSlider.value);
          const maxPosDollar = (accountSize * maxPosPercent) / 100;
          maxPositionValue.textContent =
            maxPosPercent + "% (" + formatCurrency(maxPosDollar) + ")";
        });

        // Close handlers
        closeBtn.addEventListener("click", closeAutoSignalsDialog);
        cancelBtn.addEventListener("click", closeAutoSignalsDialog);

        // Close on overlay click
        overlay.addEventListener("click", (e) => {
          if (e.target === overlay) {
            closeAutoSignalsDialog();
          }
        });

        // Close on Escape key
        document.addEventListener("keydown", (e) => {
          if (e.key === "Escape" && overlay.classList.contains("active")) {
            closeAutoSignalsDialog();
          }
        });

        // Toggle switch
        toggleSwitch.addEventListener("click", () => {
          toggleSwitch.classList.toggle("active");
        });

        // Save button
        saveBtn.addEventListener("click", async () => {
          try {
            const enabled = toggleSwitch.classList.contains("active");
            const accountSize = parseInt(accountSizeSlider.value);
            const riskPercent = parseFloat(riskPercentSlider.value);
            const maxPosition = parseFloat(maxPositionSlider.value);

            console.log(
              `🤖 ${enabled ? "Enabling" : "Disabling"} automated signals`
            );

            // Build command params - always include all parameters from sliders
            const params = {
              enableAutomatedSignals: enabled,
              accountSize: accountSize,
              riskPerTradePercent: riskPercent,
              maxPositionValuePercent: maxPosition
            };

            await sendTradingCommand(
              "toggle-automated-signals",
              currentSymbol,
              params
            );

            // Update state
            configState.automatedSignalsEnabled = enabled;
            configState.accountSize = accountSize;
            configState.riskPerTradePercent = riskPercent;
            configState.maxPositionValuePercent = maxPosition;

            // Update main UI button
            const mainToggle = document.getElementById(
              "automatedSignalsToggle"
            );
            mainToggle.classList.toggle("active", enabled);
            mainToggle.querySelector(".auto-signals-status").textContent =
              enabled ? "Enabled" : "Disabled";

            console.log(
              `✅ Automated signals ${enabled ? "enabled" : "disabled"}`,
              params
            );

            closeAutoSignalsDialog();
          } catch (error) {
            console.error(`❌ Failed to update automated signals:`, error);
            alert(`Failed to update automated signals: ${error.message}`);
          }
        });
      }

      function setupSearchDialog() {
        const overlay = document.getElementById("searchDialogOverlay");
        const input = document.getElementById("searchDialogInput");
        const closeBtn = document.getElementById("searchDialogClose");

        // Close button
        closeBtn.addEventListener("click", closeSearchDialog);

        // Close on overlay click
        overlay.addEventListener("click", (e) => {
          if (e.target === overlay) {
            closeSearchDialog();
          }
        });

        // Close on Escape key
        document.addEventListener("keydown", (e) => {
          if (e.key === "Escape" && overlay.classList.contains("active")) {
            closeSearchDialog();
          }
        });

        // Search input with API integration
        let searchTimeout;
        input.addEventListener("input", async (e) => {
          clearTimeout(searchTimeout);
          const query = e.target.value.trim();

          if (!query) {
            renderSearchResults(popularEquities, "Popular equities");
            return;
          }

          searchTimeout = setTimeout(async () => {
            const results = await searchSymbols(query);
            renderSearchResults(results, `Search results for "${query}"`);
          }, 300);
        });

        // Filter buttons
        document.querySelectorAll(".search-filter-btn").forEach((btn) => {
          btn.addEventListener("click", () => {
            document
              .querySelectorAll(".search-filter-btn")
              .forEach((b) => b.classList.remove("active"));
            btn.classList.add("active");

            // For now, just show popular equities for all filters
            // You can extend this to filter by type
            renderSearchResults(popularEquities, "Popular equities");
          });
        });
      }

      async function loadSymbol(symbol) {
        try {
          console.log(`📊 Loading ${symbol}...`);

          // Show loading overlay
          const loadingOverlay = document.getElementById("loadingOverlay");
          const loadingSymbolEl = document.getElementById("loadingSymbol");
          const chartWrapper = document.querySelector(".chart-wrapper");

          loadingSymbolEl.textContent = symbol;
          loadingOverlay.classList.add("active");
          chartWrapper.classList.add("loading");

          // Clear existing indicator lines and orders when changing symbol
          if (chart) {
            chart.clearLineSeries();
          }

          // Clear all existing order badges before loading new symbol
          badgeManager.clearAllBadges();
          console.log("🗑️ Cleared all order badges for symbol switch");

          // Build URL with replay parameters if needed
          let barsUrl = `${API_BASE_URL}/api/symbols/${symbol}/bars`;
          if (REPLAY_MODE) {
            const replayDate = getReplayDateParam();
            barsUrl += `?replayMode=true&replayDate=${replayDate}`;
            console.log(`📅 Replay mode: Loading bars before ${replayDate}`);
          }

          const response = await fetch(barsUrl);

          if (!response.ok) {
            throw new Error(`Failed to fetch bars for ${symbol}`);
          }

          const result = await response.json();
          const data = result.bars || result;
          console.log(`✅ Loaded ${data.length} bars for ${symbol}`);

          // Subscribe to WebSocket channels for this symbol
          if (wsConnected && websocketService.isAuthenticated()) {
            // Unsubscribe from previous symbol (if any)
            const prevSymbol = currentSymbol;
            if (prevSymbol && prevSymbol !== symbol) {
              websocketService.unsubscribe(`trade:${prevSymbol}`);
              websocketService.unsubscribe(`orders:${prevSymbol}`);
              websocketService.unsubscribe(`quotes:${prevSymbol}`);
              websocketService.unsubscribe(`market:${prevSymbol}`);
              websocketService.unsubscribe(`indicators:${prevSymbol}`);
              console.log(`🔕 Unsubscribed from ${prevSymbol} channels`);
            }

            // Subscribe to new symbol channels
            websocketService.subscribe(`trade:${symbol}`);
            websocketService.subscribe(`orders:${symbol}`);
            websocketService.subscribe(`quotes:${symbol}`);
            websocketService.subscribe(`market:${symbol}`);
            websocketService.subscribe(`indicators:${symbol}`);
            console.log(`✅ Subscribed to WebSocket channels for ${symbol}`);
          }

          // 🔒 CRITICAL: Close any open context menus before switching symbols
          const contextMenu = document.getElementById("context-menu");
          if (contextMenu) {
            contextMenu.style.display = "none";
            console.log("🗑️ Closed context menu on symbol switch");
          }

          // Update current symbol BEFORE rendering so loadOrders uses correct symbol
          currentSymbol = symbol;
          currentIndicators = null; // Reset indicators when symbol changes
          currentBar = null; // Reset current bar when switching symbols
          currentPrice = null; // Reset current price when switching symbols
          renderWatchlist(); // Update active state in watchlist

          // Small delay for smooth transition
          await new Promise((resolve) => setTimeout(resolve, 100));

          await renderChart(data);

          // Load orders for the new symbol (after chart is rendered)
          await loadOrders();

          // Load symbol configuration and update config panel
          await loadSymbolConfig(symbol);

          // Hide loading overlay
          loadingOverlay.classList.remove("active");
          chartWrapper.classList.remove("loading");

          // Real-time updates handled by WebSocket (quotes:${symbol} channel)
        } catch (error) {
          console.error(`❌ Error loading ${symbol}:`, error);
          console.error(`Failed to load ${symbol}`, error.message);

          // Hide loading on error
          const loadingOverlay = document.getElementById("loadingOverlay");
          const chartWrapper = document.querySelector(".chart-wrapper");
          loadingOverlay.classList.remove("active");
          chartWrapper.classList.remove("loading");

          console.error(
            "Make sure: 1. SSH tunnel is running 2. API server is running (yarn api)"
          );
        }
      }

      // Function to render chart with data
      async function renderChart(data) {
        if (data.length === 0) {
          console.error(`❌ No data available for ${currentSymbol}`);
          return;
        }

        const container = document.getElementById("chartContainer");

        // Add animation class
        container.classList.remove("animate-in");
        void container.offsetWidth; // Trigger reflow
        container.classList.add("animate-in");

        // Clean up existing chart and badge manager if any
        if (chart) {
          badgeManager.destroy();
          chart.remove();
        }

        // Create chart using TradingView Lightweight Charts library
        chart = new LightweightChart({
          container: container,
          width: container.clientWidth,
          height: container.clientHeight,
          bullishColor: "#00c805",
          bearishColor: "#ff5000",
          backgroundColor: "#000000",
          textColor: "#6e7079",
          gridColor: "rgba(42, 42, 42, 0.4)",
          // Enable smooth interactions
          layout: {
            textColor: "#6e7079",
            background: { type: "solid", color: "#000000" },
            fontSize: 12,
            fontFamily: "'Roboto', sans-serif"
          },
          rightPriceScale: {
            borderVisible: false,
            autoScale: true
          },
          leftPriceScale: {
            visible: false
          },
          timeScale: {
            borderVisible: false,
            timeVisible: true,
            secondsVisible: false
          }
        });

        // Enable smooth animated zoom (replaces jerky discrete zoom)
        chart.enableSmoothZoom();

        // Add crosshair customization - Robinhood style
        chart.getChart().applyOptions({
          crosshair: {
            mode: 1, // Normal crosshair mode
            vertLine: {
              width: 1,
              color: "rgba(255, 255, 255, 0.4)", // More visible white with transparency
              style: 0, // Solid line (0 = solid, 1 = dotted, 2 = dashed)
              labelBackgroundColor: "#2a2a2a"
            },
            horzLine: {
              width: 1,
              color: "rgba(255, 255, 255, 0.4)", // More visible white with transparency
              style: 0, // Solid line
              labelBackgroundColor: "#2a2a2a"
            }
          },
          // Enable smooth kinetic scrolling for fluid zoom/pan
          handleScroll: {
            mouseWheel: true,
            pressedMouseMove: true,
            horzTouchDrag: true,
            vertTouchDrag: true
          },
          handleScale: {
            axisPressedMouseMove: {
              time: true,
              price: true
            },
            axisDoubleClickReset: {
              time: true,
              price: true
            },
            mouseWheel: false, // Handled by enableSmoothZoom()
            pinch: true
          },
          kineticScroll: {
            touch: true,
            mouse: true
          }
        });

        // Customize price scale settings with smooth scaling
        chart
          .getSeries()
          .priceScale()
          .applyOptions({
            autoScale: true, // keep auto scaling enabled for dynamic price movements
            mode: 0, // Normal price scale mode
            invertScale: false,
            alignLabels: true,
            scaleMargins: {
              top: 0.1, // 10% empty space at top
              bottom: 0.2 // 20% empty space at bottom
            },
            borderColor: "#2a2a2a",
            entireTextOnly: false // Allow smooth price adjustments
          });

        // Customize time scale settings with smooth animations
        chart.getChart().timeScale().applyOptions({
          borderColor: "#2a2a2a",
          timeVisible: true,
          secondsVisible: false,
          shiftVisibleRangeOnNewBar: true, // Smooth shift when new bars appear
          allowShiftVisibleRangeOnWhitespaceReplacement: true,
          rightOffset: 5, // Add padding on right for better UX
          barSpacing: 6, // Comfortable bar spacing
          minBarSpacing: 3, // Minimum spacing when zoomed in
          fixLeftEdge: false,
          fixRightEdge: false,
          lockVisibleTimeRangeOnResize: true
        });

        // Convert bar data to proper format with numeric timestamps
        // and DEDUPLICATE by timestamp (keep latest for each timestamp)
        const barsByTime = new Map();
        data.forEach((bar) => {
          const timestamp = new Date(bar.timestamp).getTime();
          // Keep the latest bar for each timestamp
          barsByTime.set(timestamp, {
            symbol: bar.symbol,
            timestamp: timestamp,
            open: bar.open,
            high: bar.high,
            low: bar.low,
            close: bar.close,
            volume: bar.volume,
            vwap: bar.vwap,
            tradeCount: bar.tradeCount
          });
        });

        // Convert to sorted array
        chartData = Array.from(barsByTime.values()).sort(
          (a, b) => a.timestamp - b.timestamp
        );

        console.log(
          `📊 Loaded ${data.length} bars, deduplicated to ${chartData.length} unique bars`
        );

        // Gradually expand Y-axis range across 10 bars (positions -55 to -46 from end)
        // Instead of one giant spike bar, smoothly ramp high/low so autoscale stays clean
        const SPREAD_COUNT = 10;
        const SPREAD_START = 55; // start 55 bars from end
        if (chartData.length >= SPREAD_START) {
          const lastBar = chartData[chartData.length - 1];
          const stubPrice = currentPrice || lastBar.close;
          const maxHigh = stubPrice * 2;                        // 100% above
          const minLow = Math.max(stubPrice * 0.5, 0.10);      // 50% below, clamped

          for (let i = 0; i < SPREAD_COUNT; i++) {
            const idx = chartData.length - SPREAD_START + i;
            // Ease from 0→1 using smoothstep for natural ramp up then back down
            const t = i / (SPREAD_COUNT - 1);           // 0 to 1
            const ease = t < 0.5
              ? 2 * t * t                                // ease in
              : 1 - 2 * (1 - t) * (1 - t);              // ease out (peak at center)
            const peakT = 1 - Math.abs(2 * t - 1);      // triangle: 0→1→0 (peak at middle)

            const expandedHigh = chartData[idx].high + (maxHigh - chartData[idx].high) * peakT;
            const expandedLow = chartData[idx].low - (chartData[idx].low - minLow) * peakT;

            chartData[idx] = {
              ...chartData[idx],
              high: Math.max(chartData[idx].high, expandedHigh),
              low: Math.min(chartData[idx].low, expandedLow),
            };
          }
          console.log(
            `📊 Y-axis range spread across ${SPREAD_COUNT} bars (positions -${SPREAD_START} to -${SPREAD_START - SPREAD_COUNT + 1}): high=$${maxHigh.toFixed(2)} low=$${minLow.toFixed(2)}`
          );
        }

        // Add whitespace data for non-trading hours to improve zoom functionality
        // This creates visual gaps during market closed periods (overnight, weekends)
        const chartDataWithWhitespace = [];
        const ONE_MINUTE = 60 * 1000;
        const FIVE_MINUTES = 5 * ONE_MINUTE;
        const GAP_THRESHOLD = 10 * ONE_MINUTE; // Consider gaps > 10 minutes as market closed

        for (let i = 0; i < chartData.length; i++) {
          chartDataWithWhitespace.push(chartData[i]);

          // Check if there's a gap to the next bar
          if (i < chartData.length - 1) {
            const currentTime = chartData[i].timestamp;
            const nextTime = chartData[i + 1].timestamp;
            const gap = nextTime - currentTime;

            // If gap is significant (market closed period), add whitespace data points
            if (gap > GAP_THRESHOLD) {
              // Add whitespace at the start and end of the gap
              // This helps the chart render the gap cleanly
              chartDataWithWhitespace.push({
                timestamp: currentTime + ONE_MINUTE,
                symbol: chartData[i].symbol
                // No OHLC data = whitespace
              });

              chartDataWithWhitespace.push({
                timestamp: nextTime - ONE_MINUTE,
                symbol: chartData[i].symbol
                // No OHLC data = whitespace
              });
            }
          }
        }

        console.log(
          `📊 Added whitespace: ${chartDataWithWhitespace.length - chartData.length} whitespace points for better zoom control`
        );
        console.log(
          "📊 First 3 bars:",
          chartData.slice(0, 3).map((b) => ({
            time: b.timestamp,
            timeInSeconds: Math.floor(b.timestamp / 1000),
            open: b.open,
            close: b.close
          }))
        );

        // Set data on chart with whitespace (LightweightChart handles timestamp conversion to seconds)
        console.log(
          "📈 Calling chart.setData() with",
          chartDataWithWhitespace.length,
          "data points (",
          chartData.length,
          "bars +",
          chartDataWithWhitespace.length - chartData.length,
          "whitespace)"
        );
        try {
          chart.setData(chartDataWithWhitespace);
          console.log("✅ Chart rendered successfully with whitespace!");
        } catch (error) {
          console.error("❌ chart.setData() failed:", error);
          console.error("Error details:", {
            message: error.message,
            name: error.name,
            stack: error.stack
          });
          return;
        }

        // Update header info
        updateHeaderInfo(data);

        // Initialize BadgeManager (container already declared above)
        badgeManager.initialize(chart, container);

        // Set up badge manager callbacks for clean separation of concerns
        badgeManager.onMoveOrder = async (order, newPrice, oldPrice) => {
          if (order.type === "stop") {
            await sendTradingCommand("move-stop", currentSymbol, {
              newStopPrice: newPrice
            });
            console.log(`✅ Move stop command sent: $${newPrice}`);
          } else {
            await sendTradingCommand("update-order", currentSymbol, {
              orderId: order.id,
              newPrice: newPrice
            });
            console.log(`✅ Update order command sent: $${newPrice}`);
          }
        };

        // Subscribe to crosshair move for OHLC, price marker, and indicators
        const candleSeries = chart.getSeries();

        chart.subscribeCrosshairMove((param) => {
          const priceMarker = document.getElementById("crosshairPriceMarker");

          if (!param || !param.time) {
            // Show latest bar
            const latest = data[data.length - 1];
            updateOHLCDisplay(latest);
            updateIndicatorsLegend(null);
            // Hide price marker when no crosshair
            if (priceMarker) {
              priceMarker.classList.remove("visible");
            }
            return;
          }

          // CRITICAL: Extract candle data from the specific candle series, not from any series
          const candleData = param.seriesData.get(candleSeries);
          if (!candleData) {
            // Fallback to latest bar if we can't get data from series
            const latest = data[data.length - 1];
            updateOHLCDisplay(latest);
            updateIndicatorsLegend(null);
            if (priceMarker) {
              priceMarker.classList.remove("visible");
            }
            return;
          }

          updateOHLCDisplay(candleData);
          updateIndicatorsLegend(param);

          // Update crosshair price marker position
          if (priceMarker && param.point) {
            const price = candleData.close || candleData.value;
            if (price !== undefined && price !== null) {
              priceMarker.textContent = `$${price.toFixed(2)}`;
              priceMarker.style.top = `${param.point.y}px`;
              priceMarker.classList.add("visible");
            }
          }
        });

        // Setup controls
        setupControls(data, chart);

        // Setup context menu
        setupContextMenu(chart, data);

        // Hide crosshair price marker when mouse leaves chart
        container.addEventListener("mouseleave", () => {
          const priceMarker = document.getElementById("crosshairPriceMarker");
          if (priceMarker) {
            priceMarker.classList.remove("visible");
          }
        });

        // Note: Orders are loaded in loadSymbol() after chart renders

        // Setup and load indicators legend
        await setupIndicatorsLegend();

        // Load and plot indicator lines (1Min timeframe)
        await loadIndicatorLines();

        // Load and plot 5Min EMA indicators with cloud
        await load5MinIndicators();

        console.log("✅ Chart rendered successfully");
      }

      // ========== ORDER MANAGEMENT ==========
      // All order badge management is now handled by the BadgeManager class

      // Orders are filled by the worker - WebSocket sends order updates
      // The handleOrderUpdate function processes fills and updates badges

      // Function to load orders from API
      async function loadOrders() {
        try {
          if (!currentSymbol) {
            console.log("⚠️ No symbol selected, skipping order load");
            return;
          }

          console.log(`📋 Loading trades and orders for ${currentSymbol}...`);

          // Fetch trades with orders included for current symbol
          const response = await apiFetch(
            `${API_BASE_URL}/api/trades?symbol=${currentSymbol}&includeOrders=true`
          );

          if (!response.ok) {
            const bodyText = await response.text().catch(() => "");
            console.warn(
              `⚠️ Failed to load trades for ${currentSymbol}: ${response.status}`,
              bodyText
            );
            return;
          }

          const result = await response.json();
          const trades = result.trades || result;

          console.log(`📊 Loaded ${trades.length} trades`);

          // Clear existing order badges
          badgeManager.clearAllBadges();

          // Filter trades for current symbol and only open trades
          const symbolTrades = trades.filter((trade) => {
            const sym = String(trade.symbol || "").toUpperCase();
            const status = String(trade.status || "").toLowerCase();
            return (
              sym === String(currentSymbol).toUpperCase() && status === "open"
            );
          });

          if (symbolTrades.length === 0) {
            console.log(`📋 No open trades for ${currentSymbol}`);
            return;
          }

          console.log(
            `✅ Found ${symbolTrades.length} open trades for ${currentSymbol}`
          );

          // Update active trades tracking
          if (symbolTrades.length > 0) {
            symbolsWithActiveTrades.add(currentSymbol);
            updateWatchlistTradeIndicator(currentSymbol, true);
          } else {
            symbolsWithActiveTrades.delete(currentSymbol);
            updateWatchlistTradeIndicator(currentSymbol, false);
          }

          // Display orders from all open trades for this symbol
          symbolTrades.forEach((trade) => {
            console.log(`📊 Trade ${trade.tradeId}:`, trade);

            // Display stop loss if present
            if (
              trade.stopLoss &&
              trade.stopLoss.level &&
              trade.stopLoss.status !== "triggered"
            ) {
              const stopOrder = {
                orderId: `stop-${trade.tradeId}`,
                tradeId: trade.tradeId,
                symbol: trade.symbol,
                side: trade.position?.side === "LONG" ? "sell" : "buy",
                purpose: "stop",
                limitPrice: trade.stopLoss.level,
                quantity:
                  trade.stopLoss.remainingQty ||
                  trade.position?.remainingQty ||
                  0,
                status: "new"
              };
              handleOrderUpdate(stopOrder);
              console.log(
                `🛑 Stop loss displayed @ $${trade.stopLoss.level.toFixed(2)}`
              );
            }

            // Display orders if included
            if (trade.orders && Array.isArray(trade.orders)) {
              trade.orders.forEach((orderData) => {
                handleOrderUpdate(orderData);
              });
            }
          });
        } catch (error) {
          console.error("❌ Error loading orders:", error);
        }
      }

      // Order drag and drop is now handled by BadgeManager

      // Store current indicators
      let currentIndicators = null;

      // Function to fetch indicators from API
      async function fetchIndicators(symbol) {
        try {
          // Build URL with replay parameters if needed
          let indicatorsUrl = `${API_BASE_URL}/api/symbols/${symbol}/indicators`;
          if (REPLAY_MODE) {
            const replayDate = getReplayDateParam();
            indicatorsUrl += `?replayMode=true&replayDate=${replayDate}`;
            console.log(
              `📅 Replay mode: Loading indicators before ${replayDate}`
            );
          }

          const response = await fetch(indicatorsUrl);
          if (!response.ok) {
            console.warn(`⚠️ No indicators found for ${symbol}`);
            return null;
          }
          const result = await response.json();
          const indicators = result.indicators || result;
          console.log(`📊 Loaded indicators for ${symbol}:`, indicators);
          return indicators;
        } catch (error) {
          console.error(`❌ Error fetching indicators for ${symbol}:`, error);
          return null;
        }
      }

      // Function to setup indicators legend
      async function setupIndicatorsLegend() {
        const container = document.getElementById("chartContainer");

        // Create legend container
        const legend = document.createElement("div");
        legend.id = "indicators-legend";
        legend.style.cssText = `
          position: absolute;
          left: 12px;
          top: 12px;
          z-index: 1000;
          background: rgba(20, 20, 22, 0.95);
          backdrop-filter: blur(10px);
          border: 1.5px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          padding: 12px 16px;
          font-family: "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 12px;
          line-height: 20px;
          color: #e0e0e0;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6), 0 2px 4px rgba(0, 0, 0, 0.4);
          max-width: 300px;
          pointer-events: none;
        `;

        container.appendChild(legend);

        // Initialize legend with static data
        await initializeIndicatorsLegend();

        // Note: crosshair updates are handled by the main crosshair subscription in renderChart
      }

      // Store indicator line data for crosshair updates
      let indicatorHistoryData = new Map();

      // Function to update indicators legend with dynamic values
      function updateIndicatorsLegend(param) {
        const legend = document.getElementById("indicators-legend");
        if (!legend) return;

        // Update crosshair price marker visibility
        const priceMarker = document.getElementById("crosshairPriceMarker");
        if (priceMarker && !param) {
          priceMarker.classList.remove("visible");
        }

        let html = `
          <div style="font-weight: 700; font-size: 14px; margin-bottom: 8px; color: #fff;">
            ${currentSymbol} Indicators
          </div>
        `;

        // Get values at crosshair position or use latest
        let indicatorValues = {};

        if (param && param.time && chart) {
          // Get values at crosshair position from line series (distinct colors)
          const emaLines = [
            { key: "ema9", label: "EMA 9", color: "rgba(33, 150, 243, 1)" }, // Bright cyan-blue
            { key: "ema21", label: "EMA 21", color: "rgba(156, 39, 176, 1)" }, // Purple
            { key: "ema50", label: "EMA 50", color: "rgba(255, 152, 0, 1)" }, // Orange
            { key: "ema200", label: "EMA 200", color: "rgba(76, 175, 80, 1)" }, // Green
            { key: "vwap", label: "VWAP", color: "rgba(244, 67, 54, 1)" } // Red
          ];

          emaLines.forEach(({ key, label, color }) => {
            const series = chart.getLineSeries(key);
            if (series) {
              const data = param.seriesData.get(series);
              if (data && data.value !== undefined) {
                indicatorValues[key] = { value: data.value, label, color };
              }
            }
          });
        } else if (currentIndicators) {
          // Use latest static values
          indicatorValues = {
            ema9: {
              value: currentIndicators.ema9,
              label: "EMA 9",
              color: "rgba(33, 150, 243, 1)" // Bright cyan-blue
            },
            ema21: {
              value: currentIndicators.ema21,
              label: "EMA 21",
              color: "rgba(156, 39, 176, 1)" // Purple
            },
            ema50: {
              value: currentIndicators.ema50,
              label: "EMA 50",
              color: "rgba(255, 152, 0, 1)" // Orange
            },
            ema200: {
              value: currentIndicators.ema200,
              label: "EMA 200",
              color: "rgba(76, 175, 80, 1)" // Green
            },
            vwap: {
              value: currentIndicators.vwap,
              label: "VWAP",
              color: "rgba(244, 67, 54, 1)" // Red
            }
          };
        }

        // Display indicators
        Object.entries(indicatorValues).forEach(([key, data]) => {
          if (data && data.value !== null && data.value !== undefined) {
            const formatted =
              typeof data.value === "number"
                ? data.value.toFixed(2)
                : data.value;
            html += `
              <div style="display: flex; justify-content: space-between; margin: 4px 0;">
                <span style="opacity: 0.7;">${data.label}:</span>
                <span style="font-weight: 700; color: ${data.color};">${formatted}</span>
              </div>
            `;
          }
        });

        // Add static indicators from currentIndicators
        if (currentIndicators) {
          const staticIndicators = [
            { key: "atr", label: "ATR", format: (v) => v?.toFixed(4) },
            {
              key: "volume_ema",
              label: "Vol EMA",
              format: (v) => (v ? v.toLocaleString() : "N/A")
            },
            { key: "bar_count", label: "Bars", format: (v) => v }
          ];

          staticIndicators.forEach(({ key, label, format }) => {
            const value = currentIndicators[key];
            if (value !== undefined && value !== null) {
              const formatted = format(value);
              html += `
                <div style="display: flex; justify-content: space-between; margin: 4px 0;">
                  <span style="opacity: 0.7;">${label}:</span>
                  <span style="font-weight: 700; color: #6e7079;">${formatted}</span>
                </div>
              `;
            }
          });
        }

        legend.innerHTML = html;
      }

      // Initialize indicators legend with static data
      async function initializeIndicatorsLegend() {
        if (!currentIndicators) {
          currentIndicators = await fetchIndicators(currentSymbol);
        }
        updateIndicatorsLegend(null);
      }

      // Function to load and plot indicator lines on chart
      async function loadIndicatorLines() {
        if (!chart) return;

        try {
          // Build URL with replay parameters if needed
          let indicatorsUrl = `${API_BASE_URL}/api/symbols/${currentSymbol}/indicators`;
          if (REPLAY_MODE) {
            const replayDate = getReplayDateParam();
            indicatorsUrl += `?replayMode=true&replayDate=${replayDate}`;
          }

          // Fetch indicator history from realtime server
          const response = await fetch(indicatorsUrl);
          if (!response.ok) {
            console.warn(`⚠️ No indicator history for ${currentSymbol}`);
            return;
          }

          const result = await response.json();
          const history = result.indicators || result.history || [];
          console.log(
            `📈 Loaded ${history.length} indicator history points for ${currentSymbol}`
          );

          // Clear existing line series
          chart.clearLineSeries();

          // Define indicators to plot (distinct colors for easy scanning)
          const indicatorLines = [
            { key: "ema9", label: "EMA 9", color: "#2196F3", width: 1.5 }, // Bright cyan-blue
            { key: "ema21", label: "EMA 21", color: "#9C27B0", width: 1.5 }, // Purple
            { key: "ema50", label: "EMA 50", color: "#FF9800", width: 1.5 }, // Orange
            { key: "ema200", label: "EMA 200", color: "#4CAF50", width: 2 }, // Green - slightly thicker
            { key: "vwap", label: "VWAP", color: "#F44336", width: 1.5 } // Red
          ];

          // Create line series for each indicator with curved lines
          indicatorLines.forEach(({ key, label, color, width }) => {
            const options = {
              color: color,
              lineWidth: width || 1.5,
              lineType: 2, // Curved line type
              lastValueVisible: false, // Don't show value on price scale
              priceLineVisible: false
            };
            console.log(
              `📈 Adding ${label} with lineType=${options.lineType} (LineType.Curved), enum value=${LineType.Curved}, color=${options.color}, width=${options.lineWidth}`
            );
            console.log("Full options object:", options);
            const series = chart.addLineSeries(key, options);

            // Prepare data for this indicator - strict validation on both time and value
            const lineData = [];
            history.forEach((snapshot) => {
              if (!snapshot.values || !snapshot.timestamp) return;
              const rawValue = snapshot.values[key];
              if (rawValue === null || rawValue === undefined) return;
              const value = parseFloat(rawValue);
              if (!value || !isFinite(value) || value <= 0) return;
              const time = Math.floor(new Date(snapshot.timestamp).getTime() / 1000);
              if (!time || !isFinite(time) || isNaN(time)) return;
              lineData.push({ time, value });
            });

            // Sort and deduplicate by time
            lineData.sort((a, b) => a.time - b.time);
            const dedupedData = lineData.filter((d, i, arr) => i === 0 || d.time !== arr[i - 1].time);

            if (dedupedData.length > 0) {
              try {
                series.setData(dedupedData);
              } catch (setDataError) {
                console.warn(`⚠️ Error setting data for ${label}:`, setDataError);
                chart.removeLineSeries(key);
              }
            }
          });
        } catch (error) {
          console.error(`❌ Error loading indicator lines:`, error);
        }
      }

      // Function to load 5Min indicators (EMA21 and EMA200) with cloud
      async function load5MinIndicators() {
        if (!chart) return;

        try {
          // Build URL with replay parameters if needed for 5Min timeframe
          let indicatorsUrl = `${API_BASE_URL}/api/symbols/${currentSymbol}/indicators?timeframe=5Min`;
          if (REPLAY_MODE) {
            const replayDate = getReplayDateParam();
            indicatorsUrl += `&replayMode=true&replayDate=${replayDate}`;
          }

          console.log(`📡 Fetching 5Min indicators from: ${indicatorsUrl}`);

          // Fetch 5Min indicator history from realtime server
          const response = await fetch(indicatorsUrl);
          console.log(
            `📡 5Min API response status: ${response.status} ${response.statusText}`
          );
          if (!response.ok) {
            console.warn(`⚠️ No 5Min indicator history for ${currentSymbol}`);
            return;
          }

          const result = await response.json();
          const history = result.indicators || result.history || [];
          console.log(
            `📈 Loaded ${history.length} 5Min indicator history points for ${currentSymbol}`
          );
          console.log("📊 5Min indicators result:", result);
          console.log("📊 First 5Min indicator sample:", history[0]);

          if (history.length === 0) {
            console.warn(
              `⚠️ No 5Min indicators available for ${currentSymbol} - Redis may not have this data yet`
            );
            return;
          }

          // Prepare data for EMA21 (yellow) and EMA200 (white) - strict validation
          const ema21Data = [];
          const ema200Data = [];

          history.forEach((snapshot) => {
            if (!snapshot.timestamp) return;
            const time = Math.floor(new Date(snapshot.timestamp).getTime() / 1000);
            if (!time || !isFinite(time) || isNaN(time)) return;

            const hasValues = snapshot.values !== undefined;
            const raw21 = hasValues ? snapshot.values?.ema21 : snapshot.ema21;
            const raw200 = hasValues ? snapshot.values?.ema200 : snapshot.ema200;

            if (raw21 != null) {
              const v = parseFloat(raw21);
              if (v > 0 && isFinite(v)) ema21Data.push({ time, value: v });
            }
            if (raw200 != null) {
              const v = parseFloat(raw200);
              if (v > 0 && isFinite(v)) ema200Data.push({ time, value: v });
            }
          });

          // Sort and deduplicate
          const dedup = (arr) => {
            arr.sort((a, b) => a.time - b.time);
            return arr.filter((d, i, a) => i === 0 || d.time !== a[i - 1].time);
          };
          const safeEma21Data = dedup(ema21Data);
          const safeEma200Data = dedup(ema200Data);

          if (safeEma21Data.length === 0 || safeEma200Data.length === 0) {
            console.warn("⚠️ No valid 5Min EMA data to plot");
            return;
          }

          // Track success to decide whether to render cloud
          let ema200Ok = false;
          let ema21Ok = false;

          // Add EMA200 line (white, slim line)
          const ema200Series = chart.addLineSeries("5min_ema200", {
            color: "#FFFFFF",
            lineWidth: 2,
            lineType: 2,
            lastValueVisible: true,
            priceLineVisible: false,
            title: "EMA 200 (5M)"
          });
          try {
            ema200Series.setData(safeEma200Data);
            ema200Ok = true;
          } catch (e) {
            console.warn("⚠️ Error setting 5Min EMA200 data:", e);
            chart.removeLineSeries("5min_ema200");
          }

          // Add EMA21 line on top (bright yellow, slim line)
          const ema21Series = chart.addLineSeries("5min_ema21", {
            color: "#FFD700",
            lineWidth: 2,
            lineType: 2,
            lastValueVisible: true,
            priceLineVisible: false,
            title: "EMA 21 (5M)"
          });
          try {
            ema21Series.setData(safeEma21Data);
            ema21Ok = true;
          } catch (e) {
            console.warn("⚠️ Error setting 5Min EMA21 data:", e);
            chart.removeLineSeries("5min_ema21");
          }

          // Add green cloud between the two EMAs using area series — only if both succeeded
          if (ema21Ok && ema200Ok) {
            const cloudData = safeEma21Data.map((point) => ({
              time: point.time,
              value: point.value
            }));

            const cloudSeries = chart.addAreaSeries("5min_ema_cloud", {
              topColor: "rgba(76, 175, 80, 0.2)", // Green with 20% opacity at top
              bottomColor: "rgba(76, 175, 80, 0.05)", // Green with 5% opacity at bottom
              lineColor: "rgba(76, 175, 80, 0)", // Invisible line
              lineWidth: 0,
              lastValueVisible: false,
              priceLineVisible: false,
              crosshairMarkerVisible: false
            });

            try {
              cloudSeries.setData(cloudData);
            } catch (e) {
              console.warn("⚠️ Error setting cloud data:", e);
            }
          }
        } catch (error) {
          console.error(`❌ Error loading 5Min indicator lines:`, error);
        }
      }

      // Function to update indicators from real-time WebSocket stream
      function updateIndicatorsFromStream(data) {
        if (!chart || !data) return;

        // Safety check: only update if matches current symbol
        if (data.symbol && data.symbol !== currentSymbol) {
          return;
        }

        try {
          // data should contain indicator values and timestamp
          // Example: { timestamp: "2024-01-15T10:30:00Z", ema9: 150.5, ema21: 149.8, ... }
          const timestamp = data.timestamp
            ? Math.floor(new Date(data.timestamp).getTime() / 1000)
            : Math.floor(Date.now() / 1000);

          // List of indicators to update
          const indicators = ["ema9", "ema21", "ema50", "ema200", "vwap"];

          indicators.forEach((key) => {
            if (
              data[key] !== null &&
              data[key] !== undefined &&
              !isNaN(data[key])
            ) {
              const value = parseFloat(data[key]);

              // Only update with valid values
              if (value > 0 && isFinite(value)) {
                // Get the line series for this indicator
                const series = chart.getLineSeries(key);
                if (series) {
                  try {
                    // Update the indicator line with new value
                    series.update({
                      time: timestamp,
                      value: value
                    });
                  } catch (updateError) {
                    // Silently ignore update errors during transitions
                    if (updateError.message !== "Object is disposed" && updateError.message !== "Value is null") {
                      console.warn(`⚠️ Error updating ${key}:`, updateError);
                    }
                  }
                }
              }
            }
          });

          // Update legend with latest values
          updateIndicatorsLegend(null);
        } catch (error) {
          // Silently ignore errors during transitions
          if (error.message !== "Object is disposed") {
            console.error(`❌ Error updating indicators from stream:`, error);
          }
        }
      }

      // Initialize application
      async function initializeApp() {
        // Load active symbols from API into watchlist
        watchlistLoadPromise = loadActiveSymbols();
        await watchlistLoadPromise;

        // Note: Quote subscriptions happen in updateUserProfile() after WebSocket connects

        // Render watchlist UI
        renderWatchlist();
        setupWatchlistControls();
        setupSearchDialog();
        setupConfigPanel();
        setupAutoSignalsDialog();

        // Don't load symbols yet - wait for WebSocket to connect after auth
        // Symbol loading will happen in updateUserProfile() after WebSocket connects
      }

      // Start app + auth in parallel (prevents refresh races)
      initializeApp();
      setupAuth();

      function updateHeaderInfo(data) {
        const latest = data[data.length - 1];
        const first = data[0];
        const priceChange = latest.close - first.open;
        const percentChange = ((priceChange / first.open) * 100).toFixed(2);
        const isPositive = priceChange >= 0;

        document.getElementById("symbolName").textContent = latest.symbol;
        document.getElementById("currentPrice").textContent =
          `$${latest.close.toFixed(2)}`;

        // Defensive: keep symbol switching resilient even if DOM was previously mutated.
        // Recompute percent as number here to avoid string/number footguns.
        const percentNum = ((latest.close - first.open) / first.open) * 100;
        updatePriceChangeBadge(priceChange, percentNum);

        // Update OHLC
        updateOHLCDisplay(latest);
      }

      function updateOHLCDisplay(bar) {
        document.getElementById("ohlcOpen").textContent = bar.open.toFixed(2);
        document.getElementById("ohlcHigh").textContent = bar.high.toFixed(2);
        document.getElementById("ohlcLow").textContent = bar.low.toFixed(2);
        document.getElementById("ohlcClose").textContent = bar.close.toFixed(2);
        if (bar.volume) {
          const vol =
            bar.volume >= 1000
              ? `${(bar.volume / 1000).toFixed(2)}K`
              : bar.volume;
          document.getElementById("ohlcVolume").textContent = vol;
        }
      }

      // Helper function to generate context menu HTML with Tailwind (shared by setupControls and setupContextMenu)
      // showScaleIn: when true, adds a SCALE IN button for adding to existing positions
      function generateContextMenuHTML(menuType, price, showScaleIn = false) {
        const isBuy = menuType.includes('BUY') || menuType.includes('SCALE');
        const accent = isBuy ? '#10b981' : '#ef4444';
        return `
          <div style="text-align:center; margin-bottom:10px;">
            <div style="font-size:10px; font-weight:600; color:rgba(255,255,255,0.35); text-transform:uppercase; letter-spacing:0.1em; margin-bottom:2px;">${menuType}</div>
            <div style="font-size:20px; font-weight:700; color:#fff; letter-spacing:-0.01em;">$${price.toFixed(2)}</div>
          </div>

          <div style="margin-bottom:8px;">
            <div style="font-size:9px; font-weight:600; color:rgba(255,255,255,0.3); text-transform:uppercase; letter-spacing:0.08em; margin-bottom:5px;">Size</div>
            <div class="chip-group">
              <input type="radio" name="position" value="lotto" id="pos-lotto" checked><label for="pos-lotto">Lotto</label>
              <input type="radio" name="position" value="half" id="pos-half"><label for="pos-half">Half</label>
              <input type="radio" name="position" value="full" id="pos-full"><label for="pos-full">Full</label>
              <input type="radio" name="position" value="double" id="pos-double"><label for="pos-double">2x</label>
            </div>
          </div>

          <div style="margin-bottom:12px;">
            <div style="font-size:9px; font-weight:600; color:rgba(255,255,255,0.3); text-transform:uppercase; letter-spacing:0.08em; margin-bottom:5px;">Stop Loss</div>
            <div class="chip-group stop-loss">
              <input type="radio" name="stopLoss" value="tight" id="sl-tight"><label for="sl-tight">Tight</label>
              <input type="radio" name="stopLoss" value="usual" id="sl-usual" checked><label for="sl-usual">Usual</label>
              <input type="radio" name="stopLoss" value="wide" id="sl-wide"><label for="sl-wide">Wide</label>
            </div>
          </div>

          <button type="button" id="placeOrderBtn" class="place-order-button" style="
            width:100%; padding:8px 0; border:none; border-radius:8px; font-size:12px; font-weight:700;
            color:#fff; cursor:pointer; transition:all 0.12s ease;
            background:${accent}; box-shadow:0 2px 8px ${accent}33;
          " onmouseover="this.style.filter='brightness(1.12)'" onmouseout="this.style.filter=''" onmousedown="this.style.transform='scale(0.97)'" onmouseup="this.style.transform=''">
            Place Order
          </button>
          ${showScaleIn ? `
          <button type="button" id="scaleInBtn" style="
            width:100%; padding:8px 0; border:none; border-radius:8px; font-size:12px; font-weight:700;
            color:#fff; cursor:pointer; margin-top:6px; transition:all 0.12s ease;
            background:#7c3aed; box-shadow:0 2px 8px rgba(124,58,237,0.25);
          " onmouseover="this.style.filter='brightness(1.12)'" onmouseout="this.style.filter=''" onmousedown="this.style.transform='scale(0.97)'" onmouseup="this.style.transform=''">
            Scale In @ $${price.toFixed(2)}
          </button>
          ` : ''}
        `;
      }

      function setupControls(data, chart) {
        // Helper function to show context menu
        function showContextMenuForOrder(orderSide, price, x, y) {
          const contextMenu = document.getElementById("context-menu");
          const container = document.getElementById("chartContainer");
          const containerRect = container.getBoundingClientRect();

          let selectedPosition = "lotto";
          let selectedStopLoss = "wide";

          const menuType = orderSide === "buy" ? "BUY NOW" : "SELL NOW";

          contextMenu.innerHTML = generateContextMenuHTML(menuType, price, true);

          // Show menu temporarily to get its actual dimensions
          contextMenu.style.display = "block";
          contextMenu.style.visibility = "hidden";

          const menuWidth = contextMenu.offsetWidth;
          const menuHeight = contextMenu.offsetHeight;

          // Position menu relative to chart container
          // x, y are absolute viewport coordinates, convert to relative to chart container
          let menuLeft = x - containerRect.left + 10; // Small offset from cursor
          let menuTop = y - containerRect.top + 10;

          // Keep menu within viewport bounds
          // Check right edge
          if (menuLeft + menuWidth > containerRect.width) {
            menuLeft = x - containerRect.left - menuWidth - 10; // Position to left of cursor
          }

          // Check bottom edge
          if (menuTop + menuHeight > containerRect.height) {
            menuTop = containerRect.height - menuHeight - 10; // Position above
          }

          // Check left edge
          if (menuLeft < 0) {
            menuLeft = 10;
          }

          // Check top edge
          if (menuTop < 0) {
            menuTop = 10;
          }

          console.log("📍 Context menu positioning:", {
            buttonX: x,
            buttonY: y,
            menuWidth,
            menuHeight,
            containerWidth: containerRect.width,
            containerHeight: containerRect.height,
            menuLeft,
            menuTop
          });

          contextMenu.style.left = `${menuLeft}px`;
          contextMenu.style.top = `${menuTop}px`;
          contextMenu.style.visibility = "visible";

          // Setup radio buttons for position size
          const positionRadios = contextMenu.querySelectorAll(
            'input[name="position"]'
          );
          selectedPosition = "lotto"; // Default

          positionRadios.forEach((radio) => {
            radio.onchange = (e) => {
              selectedPosition = e.target.value;
              console.log(`📊 Position size changed to: ${selectedPosition}`);
            };
          });

          // Setup radio buttons for stop loss
          const stopLossRadios = contextMenu.querySelectorAll(
            'input[name="stopLoss"]'
          );
          selectedStopLoss = "usual"; // Default

          stopLossRadios.forEach((radio) => {
            radio.onchange = (e) => {
              selectedStopLoss = e.target.value;
              console.log(`🛑 Stop loss changed to: ${selectedStopLoss}`);
            };
          });

          // Place order button - send buy-now command
          contextMenu.querySelector("#placeOrderBtn").onclick = async (evt) => {
            evt.stopPropagation();

            try {
              // For Buy button, just send buy-now (market order)
              const result = await sendTradingCommand(
                "buy-now",
                currentSymbol,
                {
                  sizing: selectedPosition,
                  stopLoss: selectedStopLoss
                }
              );

              console.log(`✅ ${menuType} Order Sent:`, {
                symbol: currentSymbol,
                sizing: selectedPosition,
                stopLoss: selectedStopLoss,
                commandId: result.commandId
              });
            } catch (error) {
              console.error(`❌ Failed to place order:`, error);
            }

            contextMenu.style.display = "none";
          };

          // Scale In button (market) - send scale-in command without price (market order)
          const scaleInBtnBuy = contextMenu.querySelector("#scaleInBtn");
          if (scaleInBtnBuy) {
            scaleInBtnBuy.onclick = async (evt) => {
              evt.stopPropagation();

              try {
                // Scale-in without price = market order at current price
                const result = await sendTradingCommand(
                  "scale-in",
                  currentSymbol,
                  {
                    sizing: selectedPosition,
                    stopLoss: selectedStopLoss
                  }
                );

                console.log(`✅ SCALE IN (Market) Order Sent:`, {
                  symbol: currentSymbol,
                  sizing: selectedPosition,
                  stopLoss: selectedStopLoss,
                  commandId: result.commandId
                });
              } catch (error) {
                console.error(`❌ Failed to place scale-in order:`, error);
              }

              contextMenu.style.display = "none";
            };
          }
        }

        // 🔧 CRITICAL FIX: Remove old handlers before adding new ones to prevent duplicate commands
        const buyBtn = document.getElementById("buyBtn");
        const sellBtn = document.getElementById("sellBtn");
        const symbolSearch = document.getElementById("symbolSearch");

        if (buyButtonHandler) {
          buyBtn.removeEventListener("click", buyButtonHandler);
          console.log("🗑️ Removed old buy button handler");
        }

        if (sellButtonHandler) {
          sellBtn.removeEventListener("click", sellButtonHandler);
          console.log("🗑️ Removed old sell button handler");
        }

        if (symbolSearchHandler) {
          symbolSearch.removeEventListener("click", symbolSearchHandler);
          console.log("🗑️ Removed old symbol search handler");
        }

        // Buy button - show context menu
        buyButtonHandler = (e) => {
          e.stopPropagation();
          // Use currentPrice (live quote) or fallback to last bar close
          const price = currentPrice || data[data.length - 1].close;
          const btnRect = e.target.getBoundingClientRect();
          console.log(
            "Buy button clicked, price:",
            price,
            "currentPrice:",
            currentPrice,
            "button rect:",
            btnRect
          );
          // Use absolute screen position
          showContextMenuForOrder("buy", price, btnRect.left, btnRect.bottom);
        };
        buyBtn.addEventListener("click", buyButtonHandler);

        // Sell button - send close-now command to exit position
        sellButtonHandler = async (e) => {
          e.stopPropagation();

          if (!currentSymbol) {
            console.warn("⚠️ No symbol selected");
            return;
          }

          try {
            const result = await sendTradingCommand(
              "close-now",
              currentSymbol,
              {
                reason: "Manual close from UI"
              }
            );
            console.log(`✅ Close Position Sent:`, {
              symbol: currentSymbol,
              commandId: result.commandId
            });
          } catch (error) {
            console.error(`❌ Failed to close position:`, error);
          }
        };
        sellBtn.addEventListener("click", sellButtonHandler);

        // Symbol search - open search dialog
        symbolSearchHandler = () => {
          openSearchDialog();
        };
        symbolSearch.addEventListener("click", symbolSearchHandler);
      }

      function setupContextMenu(chart, data) {
        const container = document.getElementById("chartContainer");
        const contextMenu = document.getElementById("context-menu");
        const latestPrice = data[data.length - 1].close;

        let selectedPosition = "lotto";
        let selectedStopLoss = "wide";

        // Remove old context menu handler if exists
        if (contextMenuHandler) {
          container.removeEventListener("contextmenu", contextMenuHandler);
          console.log("🗑️ Removed old context menu handler");
        }

        // Create new handler
        contextMenuHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log("🖱️ Right-click detected on chart");

          // Don't show menu if chart is not ready
          if (!chart || !currentSymbol) {
            console.warn("⚠️ Chart not ready, ignoring right-click");
            return;
          }

          const chartApi = chart.getChart();
          const series = chart.getSeries();

          if (!series) {
            console.warn("⚠️ Chart series not ready, ignoring right-click");
            return;
          }

          const rect = container.getBoundingClientRect();
          const y = e.clientY - rect.top;

          let clickedPrice;
          try {
            clickedPrice = series.coordinateToPrice(y);

            // Validate the clicked price is within a reasonable range of current price
            const validationPrice = currentPrice || data[data.length - 1].close;
            const maxDeviation = validationPrice * 0.5; // 50% deviation threshold

            if (!clickedPrice || isNaN(clickedPrice) || clickedPrice <= 0) {
              console.warn(
                "⚠️ coordinateToPrice returned invalid value, using current price"
              );
              clickedPrice = validationPrice;
            } else if (
              Math.abs(clickedPrice - validationPrice) > maxDeviation
            ) {
              console.warn(
                `⚠️ coordinateToPrice returned suspicious value $${clickedPrice.toFixed(2)} vs current $${validationPrice.toFixed(2)}, using current price`
              );
              clickedPrice = validationPrice;
            }
          } catch (err) {
            console.warn("⚠️ coordinateToPrice failed:", err);
            clickedPrice = currentPrice || data[data.length - 1].close;
          }

          // Use currentPrice (updated from quotes/trades) instead of latestPrice for accurate comparison
          const referencePrice = currentPrice || latestPrice;
          const isBelowPrice = clickedPrice < referencePrice;
          const menuType = isBelowPrice ? "BUY DIP" : "BUY BREAK";

          console.log(
            `📍 Context menu: clicked=$${clickedPrice.toFixed(
              2
            )}, current=$${referencePrice?.toFixed(2)}, type=${menuType}`
          );

          // Build menu (show SCALE IN button on chart right-click for adding to existing positions)
          contextMenu.innerHTML = generateContextMenuHTML(
            menuType,
            clickedPrice,
            true // showScaleIn - always show on chart right-click, backend validates if position exists
          );

          // Show menu temporarily to get its actual dimensions
          contextMenu.style.display = "block";
          contextMenu.style.visibility = "hidden";

          const menuWidth = contextMenu.offsetWidth;
          const menuHeight = contextMenu.offsetHeight;

          // Position menu relative to chart container
          let menuLeft = e.clientX - rect.left + 10;
          let menuTop = e.clientY - rect.top + 10;

          // Keep menu within viewport bounds
          // Check right edge
          if (menuLeft + menuWidth > rect.width) {
            menuLeft = e.clientX - rect.left - menuWidth - 10;
          }

          // Check bottom edge
          if (menuTop + menuHeight > rect.height) {
            menuTop = rect.height - menuHeight - 10;
          }

          // Check left edge
          if (menuLeft < 0) {
            menuLeft = 10;
          }

          // Check top edge
          if (menuTop < 0) {
            menuTop = 10;
          }

          contextMenu.style.left = `${menuLeft}px`;
          contextMenu.style.top = `${menuTop}px`;
          contextMenu.style.visibility = "visible";

          // Reset selections
          selectedPosition = "lotto";
          selectedStopLoss = "usual";

          // Setup radio buttons for position size
          const positionRadios = contextMenu.querySelectorAll(
            'input[name="position"]'
          );

          positionRadios.forEach((radio) => {
            radio.onchange = (e) => {
              selectedPosition = e.target.value;
              console.log(`📊 Position size changed to: ${selectedPosition}`);
            };
          });

          // Setup radio buttons for stop loss
          const stopLossRadios = contextMenu.querySelectorAll(
            'input[name="stopLoss"]'
          );

          stopLossRadios.forEach((radio) => {
            radio.onchange = (e) => {
              selectedStopLoss = e.target.value;
              console.log(`🛑 Stop loss changed to: ${selectedStopLoss}`);
            };
          });

          // Place order button - send buy-dip or buy-on-break command
          contextMenu.querySelector("#placeOrderBtn").onclick = async (evt) => {
            evt.stopPropagation();

            // Re-validate order type against current price at time of placement
            const currentReferencePrice = currentPrice || latestPrice;
            const isStillBelowPrice = clickedPrice < currentReferencePrice;
            const orderType = isStillBelowPrice ? "BUY DIP" : "BUY BREAK";
            const action = isStillBelowPrice ? "buy-dip" : "buy-on-break";

            try {
              const result = await sendTradingCommand(action, currentSymbol, {
                price: clickedPrice,
                sizing: selectedPosition,
                stopLoss: selectedStopLoss
              });

              console.log(`✅ ${orderType} Order Sent:`, {
                symbol: currentSymbol,
                price: clickedPrice,
                currentMarketPrice: currentReferencePrice,
                sizing: selectedPosition,
                stopLoss: selectedStopLoss,
                commandId: result.commandId
              });
            } catch (error) {
              console.error(`❌ Failed to place order:`, error);
            }

            contextMenu.style.display = "none";
          };

          // Scale In button - send scale-in command at the clicked price
          const scaleInBtn = contextMenu.querySelector("#scaleInBtn");
          if (scaleInBtn) {
            scaleInBtn.onclick = async (evt) => {
              evt.stopPropagation();

              try {
                const result = await sendTradingCommand("scale-in", currentSymbol, {
                  price: clickedPrice,
                  sizing: selectedPosition,
                  stopLoss: selectedStopLoss
                });

                console.log(`✅ SCALE IN Order Sent:`, {
                  symbol: currentSymbol,
                  price: clickedPrice,
                  sizing: selectedPosition,
                  stopLoss: selectedStopLoss,
                  commandId: result.commandId
                });
              } catch (error) {
                console.error(`❌ Failed to place scale-in order:`, error);
              }

              contextMenu.style.display = "none";
            };
          }

          return false; // Prevent browser context menu
        };

        // Add the new handler
        container.addEventListener("contextmenu", contextMenuHandler);
        console.log("✅ Added new context menu handler");

        // Remove old click-outside handler if exists
        if (contextMenuClickOutsideHandler) {
          document.removeEventListener("click", contextMenuClickOutsideHandler);
        }

        // Hide on click outside (but not when clicking inside the menu)
        contextMenuClickOutsideHandler = (e) => {
          if (!contextMenu.contains(e.target)) {
            contextMenu.style.display = "none";
          }
        };
        document.addEventListener("click", contextMenuClickOutsideHandler);
      }

      // Cleanup on page unload
      window.addEventListener("beforeunload", () => {
        if (wsConnected) {
          websocketService.disconnect();
        }
      });
    